import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1$1 from '@upr-web-primer/dal';
import { TokenStatus, DalModule } from '@upr-web-primer/dal';
import * as i6 from '@upr-web-primer/notification';
import { NotificationModule } from '@upr-web-primer/notification';
import * as i1 from '@upr-web-primer/page-labels';
import { PageLabelsModule } from '@upr-web-primer/page-labels';
import * as i5 from '@upr-web-primer/spinner';
import { SpinnerModule } from '@upr-web-primer/spinner';
import * as i3 from '@upr-web-primer/app-utils';
import { paramBool, sendEvent, genericRetryStrategy } from '@upr-web-primer/app-utils';
import { isEmpty, get, isBoolean, pickBy, negate, isUndefined, values, includes, sortBy, map, find } from 'lodash-es';
import { BehaviorSubject, take, Subscription, filter, scan, firstValueFrom, from, switchMap, retry, catchError } from 'rxjs';
import * as i2 from '@upr-web-primer/locale';
import * as i4 from '@upr-web-primer/storage';
import { HttpHeaders } from '@angular/common/http';
import { v4 } from 'uuid';

/**
 * Export CacheKeys
 * @type {{}}
 */
const CacheKeys = {
  MARKETING_ID: 'marketing_id',
  InteractGuestId: 'InteractGuestId',
  GUESTS_E_TAG: 'GUESTS-ETAG',
  GUEST_LOCATION_ID: 'GUEST_LOCATION_ID',
  LOGGEDIN_GUEST_ID: 'LOGGEDIN_GUEST_ID',
  LOGGEDIN_USER_ID: 'LOGGEDIN_USER_ID',
  IS_SIGN_IN: 'IS_SIGN_IN',
  EMAIL_USER_ID: 'EMAIL_USER_ID',
  OAUTH_LOGIN: 'oAuthLogin',
  OAUTH_USER_NAME: 'oAuthLoginUserName',
  OAUTH_LOGIN_GID: 'OAUTH_LOGIN_GID',
  USER_L_DAP: 'USER_Ldap',
  USER_F_NAME: 'USER_FNAME',
  USER_L_NAME: 'USER_LNAME',
  USER_SELECTED_COUNTRY: 'USER_SELECTED_COUNTRY',
  RECEIVE_UPDATES: 'receiveUpdates',
  RETAIN_NOTIFICATION: 'RETAIN_NOTIFICATION',
  STORE_ID: 'STORE_ID',
  CONTRACT_ID: 'CONTRACT_ID',
  HEADERS_WC_TOKEN: 'HEADERS_WC_TOKEN',
  HEADERS_WC_TRUSTED_TOKEN: 'HEADERS_WC_TRUSTED_TOKEN',
  PHONE_1_TYPE: 'PHONE_1_TYPE',
  ZIP_CODE: 'zip_code',
  PROMPT_TO_VERIFY_EMAIL: 'promptToVerifyEmail',
  EMAIL_TEXT_MESSAGE: 'emailTextMessage',
  EMAIL_CHANGED: 'EMAIL_CHANGED'
};
const GuestsUrls = {
  GUESTS: {
    // GET - SEARCH FOR GUESTS BY USERNAME
    // POST - CREATE GUEST
    BASE: '/guests',
    // GET - SPECIFIC GUEST
    // PUT - UPDATE GUEST
    // DELETE - GUEST
    WITH_ID: '/guests/{guest_id}',
    // GET - RETRIEVE REFERENCES
    AFFILIATIONS: '/guests/{guest_id}/affiliations',
    // GET - RETRIEVE ALL AFFILIATIONS RELATED TO GUEST
    TRAVEL_PARTY_AFFILIATIONS: '/guests/{guest_id}/travel-party-affiliations',
    // GET - RETRIEVE ACCOUNT AFFILIATIONS
    ACCOUNT_AFFILIATIONS: '/guests/{guest_id}/account-affiliations',
    // GET - PREFERENCES FOR GUEST
    // POST - CREATE PREFERENCE
    // PUT - UPDATE PREFERENCE
    PREFERENCES: '/guests/{guest_id}/preferences',
    // POST - UPDATE GUESTS ACCOUNT USER ID AND EMAIL ADDRESS
    USER_ID_UPDATE: '/guests/{guest_id}/user-id-update',
    // GET - PERSONAL INFO ON GUEST
    PERSONAL_INFO: '/guests/{guest_id}/personal-info',
    // GET - ALL ADDRESSES FOR PROFILE
    PERSONAL_INFO_ADDRESSES: '/guests/{guest_id}/personal-info/addresses',
    // GET - SINGLE ADDRESS FOR PROFILE
    // PUT - UPDATE SINGLE
    // DELETE - DELETE SINGLE
    PERSONAL_INFO_ADDRESSES_SINGLE: '/guests/{guest_id}/personal-info/addresses/{address_id}',
    // GET - CONTACT INFO FOR GUEST
    // PUT - UPDATE CONTACT INFO
    PERSONAL_INFO_CONTACTS: '/guests/{guest_id}/personal-info/contacts',
    // GET - ALL EMAIL INFO FOR GUEST
    // POST - CREATE EMAIL FOR GUEST
    PERSONAL_INFO_EMAILS: '/guests/{guest_id}/personal-info/emails',
    // GET - SINGLE EMAIL FOR GUEST
    // PUT - UPDATE SINGLE EMAIL
    // DELETE - DELETE SINGLE EMAIL
    PERSONAL_INFO_EMAILS_SINGLE: '/guests/{guest_id}/personal-info/emails/{email_address}',
    // GET - ALL PHONE INFO FOR GUEST
    // POST - CREATE PHONE FOR GUEST
    PERSONAL_INFO_PHONES: '/guests/{guest_id}/personal-info/phones',
    // GET - SINGLE PHONE FOR GUEST
    // PUT - UPDATE SINGLE PHONE
    // DELETE - DELETE SINGLE PHONE
    PERSONAL_INFO_PHONES_SINGLE: '/guests/{guest_id}/personal-info/phones/{phone_number}'
  },
  INSTANCES: {
    // GET - INSTANCE OBJECT BY DEVICE IDENTIFIER
    // POST - CREATE APP INSTANCE
    CREATE: '{base_url_secondary}/instances'
  },
  MARKETING_IDS: {
    // GET - MARKETING ID BASED ON REFERENCE ID
    RETRIEVE: '/marketing-ids',
    // GET - PREFERENCE FOR GUEST PROFILE
    // PUT - CREATE / UPDATE MARKETING ID REFERENCE
    // DELETE - MARKETING ID
    WITH_ID: '/marketing-ids/{marketing_id}'
  },
  AUTH: {
    // GET - TRIGGER PROCESS FOR RESET PASSWORD
    FORGOT_PASSWORD: '/cred/auth/forgotPassword/{user_id}',
    // PUT - CONTINUES PROCESS FROM FORGOT PASSWORD. {request_id} FROM FORGOT PASSWORD CALL.
    RESET_PASSWORD: '/cred/auth/resetPassword/{user_id}/{request_id}',
    // PUT - CHANGE USER PASSWORD
    CHANGE_PASSWORD: '/cred/auth/changePassword/{user_id}'
  },
  ENTITLEMENTS: {
    ASSIGNEES: '/guest/TravelParty/retrieveAssignedNames?guestId=',
    CREATE: '/guest/TravelParty/createAssignee',
    LINK: '/et/Entitlements/link',
    ENTITLEMENTS_LINK_METHODS: '/et/Entitlements/link/methods',
    ENTITLEMENTS_LINK_SEARCH_LINK: '/et/Entitlements/search/link'
  },
  FOLIO: {
    WALLET: '/inv/Orders/wallet'
  },
  COMMERCE: {
    SHIPPING_ADDRESS_SAVE: '/shop/wcs/resources/store/{store_id}/person/@self/contact',
    // POST - GET COMMERCE TOKENS <--- Can be removed and replaced with new ms-cart when deployed.
    AUTHN: '/guest/GuestProfiles/commerce/authN'
  },
  TRIDION: {
    ID_EQV_NAME: '/cp/content/pages?ids='
  },
  // POST - CREATE ACCOUNT
  ACCOUNT_ENROLL: '/enroll-account',
  // DELETE - DELETE SPECIFIC ACCOUNT
  ACCOUNT_DELETE: '/enroll-account/{user_id}',
  //POST - UNENROLL GUEST ACCOUNT
  UNENROLL_ACCOUNT: '/guests/{guest_id}/unenroll-account',
  // POST - CREATE CONTACT
  CREATE_CONTACT: '/create-contact',
  // POST - CAPTURE PROFILE FOR EMAIL OPT-IN/OUT
  EMAIL_OPT_IN_OUT: '/email-opt-in-out',
  // POST - UPGRADE GUEST PROFILE TO ACCOUNT WITH ORDER NUMBER
  ORDER_PROFILE_TO_ACCOUNT: '/order-profile-to-account',
  // POST - Enhance a guest profile, directly into an account
  PROFILE_TO_ACCOUNT: '/profile-to-account',
  // GET - Get Contract ID <--- Can be removed and replaced with new ms-cart when deployed.
  CONTRACT: '/shop/wcs/resources/store/{store_id}/contract?q=eligible',
  // POST - Validate Email givin.
  VALIDATE_EMAIL: '/utils/utilities/validateEmail',
  // GET - CHECK TO SEE IF EMAIL IS AVAILABLE
  ACCOUNT_AVAILABLE: '/check-account',
  // PUT - OLD GUEST CREATE UNREGISTERED
  CREATE_UNREGISTERED: '/guest/GuestProfiles/unregistered',
  // PUT - OLD GUEST ORDER TO PROFILE
  CREATE_LINKED_ACCOUNT: '/guest/GuestProfiles/createLinkedAccount',
  WALLET: {
    PAYMENT_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}',
    PAYMENT_CARDS: '/guests/{guest_id}/payment-cards',
    UPDATE_PAYMENT_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}',
    MAKE_PRIMARY_CARD: '/guests/{guest_id}/payment-cards/{payment_card_id}/make-primary',
    SET_PIN: '/guests/{guest_id}/pin',
    HAS_PIN: '/guests/{guest_id}/has-pin',
    //POST - LINK GUEST TICKET
    LINK_TICKET: '/guests/{guest_id}/link-ticket',
    //GET - GUEST TICKETS
    TICKETS: '/guests/{guest_id}/guest-travel-party-member-tickets?resort-area-code={resort_area_code}'
  },
  TRAVEL_PARTY: {
    SET_SPENDING_LIMIT: '/guests/{guest_id}/travel-party-members/{travel_party_member_id}/spending-limit',
    TRANSACTIONS: '/guests/{guest_id}/travel-party-members-receipts',
    //GET - GUEST TRAVEL PARTY MEMBERS
    TRAVEL_PARTY_MEMBERS: '/guests/{guest_id}/travel-party-members',
    UPDATE_TRAVEL_PARTY_MEMBERS: '/guests/{guest_id}/travel-party-members/{travel_party_member_id}'
  }
};
const USHSourceIds = {
  en: {
    WebCard: '1012025',
    WebSpendLimit: '1012026',
    WebPIN: '1012027',
    WebTravelPartyMemeber: '1012028',
    WebAccountCreation: '1012000',
    WebAssignEntitlements: '1012002',
    WebPurchaseConfirmation: '1012003',
    WebEditCommunications: '1012004',
    WebEditContactInfo: '1012005',
    WebEditPersonalInfo: '1012006',
    WebEditAddress: '1012007',
    WebCheckout: '1012008',
    WebEmailUnsubOrEditInterest: '1012023',
    WebLinkEntitlements: '1012024',
    WebNewsLetter: {
      default: '1012010',
      footer: '1012011',
      wwohp: '1012012',
      hhn: '1012013',
      mg: '1012014',
      kingkong: '1012015',
      rtu: '1012018',
      cw: '1012019',
      fastandfurious: '1012030',
      'fast-furious-supercharged': '1012030'
    },
    WebCreateUnregisteredUser: '1010002',
    WebCheckoutCreateUnregisteredUser: '1012008',
    WebPasswordUpdate: '1012063',
    WebPasswordReset: '1012061',
    WebForgotPassword: '1012060',
    WebAccountDelete: '1012066',
    WebUSHDiningReservation: '1012103',
    MobileUSHDiningReservation: '1016101'
  },
  es: {
    WebEditCommunications: '3002004',
    WebEditContactInfo: '3002005',
    WebEditPersonalInfo: '3002006',
    WebEditAddress: '3002007',
    WebEmailUnsubOrEditInterest: '3002023',
    WebNewsLetter: {
      default: '3002010',
      footer: '3002011',
      wwohp: '3002012',
      hhn: '3002013',
      mg: '3002014',
      kingkong: '3002015',
      vb: '3002016',
      hotels: '3002017',
      rtu: '3002018',
      cw: '3002019',
      ioa: '3002020',
      usf: '3002021',
      bmg: '3002022',
      fastandfurious: '3002030'
    },
    WebCreateUnregisteredUser: '3000002',
    WebCheckoutCreateUnregisteredUser: '3002008'
  }
};
const SourceIds = {
  en: {
    WebCard: '1002025',
    WebSpendLimit: '1002026',
    WebPIN: '1002027',
    WebTravelPartyMemeber: '1002028',
    WebAccountCreation: '1002001',
    WebAssignEntitlements: '1002002',
    WebPurchaseConfirmation: '1002003',
    WebEditCommunications: '1002004',
    WebEditContactInfo: '1002005',
    WebEditPersonalInfo: '1002006',
    WebEditAddress: '1002007',
    WebCheckout: '1002008',
    WebEmailUnsubOrEditInterest: '1002023',
    WebLinkEntitlements: '1002024',
    WebNewsLetter: {
      default: '1002010',
      footer: '1002011',
      wwohp: '1002012',
      hhn: '1002013',
      mg: '1002014',
      kingkong: '1002015',
      vb: '1002016',
      hotels: '1002017',
      rtu: '1002018',
      cw: '1002019',
      ioa: '1002020',
      usf: '1002021',
      bmg: '1002022',
      fastandfurious: '1002030',
      'fast-furious-supercharged': '1002030'
    },
    WebCreateUnregisteredUser: '1000002',
    WebCheckoutCreateUnregisteredUser: '1002008',
    WebPasswordUpdate: '1002063',
    WebPasswordReset: '1002061',
    WebForgotPassword: '1002060',
    WebAccountDelete: '1002066',
    WebDiningReservation: '1002103',
    MobileDiningReservation: '1006101'
  },
  es: {
    WebEditCommunications: '3002004',
    WebEditContactInfo: '3002005',
    WebEditPersonalInfo: '3002006',
    WebEditAddress: '3002007',
    WebEmailUnsubOrEditInterest: '3002023',
    WebNewsLetter: {
      default: '3002010',
      footer: '3002011',
      wwohp: '3002012',
      hhn: '3002013',
      mg: '3002014',
      kingkong: '3002015',
      vb: '3002016',
      hotels: '3002017',
      rtu: '3002018',
      cw: '3002019',
      ioa: '3002020',
      usf: '3002021',
      bmg: '3002022',
      fastandfurious: '3002030'
    },
    WebCreateUnregisteredUser: '3000002',
    WebCheckoutCreateUnregisteredUser: '3002008'
  }
};
const DefaultValue = {
  PRIMARY_SHIPPING_ADDRESS_INDICATOR: 'Y',
  CURRENCY_CODE: 'usd'
};
const MS_GUESTS_REPLACED_DEFAULTS = {
  ms_guests: {
    cart_enabled: paramBool('$ms_guests.cart_enabled$'),
    skip_instance: paramBool('$ms_guests.skip_instance$'),
    generate_local: paramBool('$ms_guests.generate_local$')
  }
};
const MS_GUESTS_ENV_DEFAULTS = {
  ms_guests: {
    cart_enabled: true,
    skip_instance: false,
    generate_local: true
  }
};

// ENUMS
var PhoneType;
(function (PhoneType) {
  PhoneType["cell"] = "cell";
  PhoneType["work"] = "work";
  PhoneType["home"] = "home";
})(PhoneType || (PhoneType = {}));
var StatusType;
(function (StatusType) {
  StatusType["active"] = "active";
  StatusType["inactive"] = "inactive";
})(StatusType || (StatusType = {}));
var EmailType;
(function (EmailType) {
  EmailType["personal"] = "personal";
  EmailType["work"] = "work";
})(EmailType || (EmailType = {}));
var AddressType;
(function (AddressType) {
  AddressType["home"] = "home";
  AddressType["work"] = "work";
  AddressType["shipping"] = "shipping";
  AddressType["billing"] = "billing";
  AddressType["generic"] = "generic";
})(AddressType || (AddressType = {}));

/**
 * Export MsGuestBaseHelper
 */
class MsGuestBaseHelper {
  constructor(pageLabelsService, localeService, appEnvSrv, localStorage, sessionStorage, spinner, notification, defaultValues, appDefaults, relativeUrls, tridionSite) {
    this.pageLabelsService = pageLabelsService;
    this.localeService = localeService;
    this.appEnvSrv = appEnvSrv;
    this.localStorage = localStorage;
    this.sessionStorage = sessionStorage;
    this.spinner = spinner;
    this.notification = notification;
    this.defaultValues = defaultValues;
    this.appDefaults = appDefaults;
    this.relativeUrls = relativeUrls;
    this.tridionSite = tridionSite;
    /**
     * Promise control object
     * @type {{resolve: (response?: any) => Promise<any | void>; reject: (msg?: string) => Promise<any | void>}}
     */
    this.promise = {
      resolve: response => {
        return response ? Promise.resolve(response) : Promise.resolve();
      },
      reject: (msg = '', obj) => {
        if (!isEmpty(obj)) {
          msg += JSON.stringify(obj);
        }
        return Promise.reject(msg);
      }
    };
    this.$emailInValidation = new BehaviorSubject('');
    this.$validationTimeout = new BehaviorSubject(false);
    this.loggedInStatusChanged = false;
    this._loggedInStatus = new BehaviorSubject(false);
    this._instanceLoaded = new BehaviorSubject(undefined);
    this.appEnvSrv.addDefaultValues(MS_GUESTS_ENV_DEFAULTS, MS_GUESTS_REPLACED_DEFAULTS);
    this.envConfig = this.appEnvSrv.getEnvVariables('ms_guests');
    this.loggedInStatus$ = this._loggedInStatus.asObservable();
    this.instanceLoaded$ = this._instanceLoaded.asObservable();
    if (this.pageLabelsService) {
      this.pageLabelsService.get().then(data => this.pageLabels = data);
    }
    if (isEmpty(this.defaultValues)) {
      this.consoleError('Please add values to injected provider: DefaultValues @' + this.constructor.name);
    }
    if (isEmpty(this.relativeUrls)) {
      this.consoleError('Please add values to injected provider: RelativeUrls @' + this.constructor.name);
    }
    this.instanceLoaded$.pipe(take(1)).subscribe(() => this.updateLoggedInStatus(this.isAuth()));
  }
  /**
   * Show the error on UI
   * @param  {string} error
   * @param {success|error} state
   * @return string Return the passed error or the found error.
   */
  showLabel(error, state) {
    const found = this.findLabel(error);
    this.notification.show(found, state);
    return found;
  }
  /**
   * Helper function to find the page label error
   * @param  {string} error
   * @return string returns the page label error, otherwise return the passed in error.
   */
  findLabel(error) {
    if (this.pageLabels && this.pageLabels[error]) {
      return this.pageLabels[error];
    }
    return error;
  }
  /**
   * Get instance id
   * @return string
   */
  getInstanceId() {
    return this.localStorage.getItem(CacheKeys.InteractGuestId);
  }
  /**
   * Console Error
   * @param args
   */
  consoleError(...args) {
    return console.error(...args);
  }
  /**
   * Check Guest Headers
   * @param {ILooseObject} response
   * @returns {Promise<HttpResponse<ILooseObject>>}
   */
  checkGuestHeaders(response) {
    if (isEmpty(get(response, 'headers', undefined))) {
      return this.promise.reject('No headers available to set: ', response);
    }
    return this.promise.resolve(response);
  }
  /**
   * Set Instance Id
   * @param {string} instanceId
   */
  setInstanceId(instanceId) {
    if (!instanceId) {
      return this.consoleError('Failed to set InteractGuestId: ', instanceId);
    }
    this.localStorage.setItem(CacheKeys.InteractGuestId, instanceId);
  }
  /**
   * Set E tag
   * @param {string} etag
   */
  setEtag(etag) {
    if (!etag) {
      this.consoleError('Failed to set GUESTS-ETAG: ', etag);
    }
    this.localStorage.setItem(CacheKeys.GUESTS_E_TAG, etag);
  }
  /**
   * Catch Promise Error
   * @param error
   * @returns {Promise<any>}
   */
  catchError(error) {
    this.consoleError('ISSUE WITH MS-GUESTS CALL --->', get(error, 'message', undefined) || error);
    return this.promise.reject(error);
  }
  /**
   * Get No cache headers
   * @returns {HttpHeaders}
   */
  getNoCacheHeaders() {
    return {
      'x-uip': 'Web',
      'x-source-id': 'Web',
      Pragma: 'no-cache',
      'Cache-Control': 'no-store'
    };
  }
  /**
   * Get login guest id
   * @returns {string}
   */
  getGuestId() {
    const guestId = this.localStorage.getItem(CacheKeys.LOGGEDIN_GUEST_ID) || this.localStorage.getItem(CacheKeys.OAUTH_LOGIN_GID);
    if (!this.isLoggedIn() || !guestId) {
      return '';
    }
    return guestId;
  }
  /**
   * Get login guest id
   * @returns {string}
   */
  getOAuthGuestID() {
    const guestId = this.localStorage.getItem(CacheKeys.OAUTH_LOGIN_GID);
    if (!this.isLoggedIn() || !guestId) {
      return '';
    }
    return guestId;
  }
  /**
   * Get login user name (email)
   * @returns {string}
   */
  getLoginUserName() {
    let email = '';
    if (this.isLoggedIn()) {
      const guestEmail = this.localStorage.getItem(CacheKeys.OAUTH_USER_NAME) || this.localStorage.getItem(CacheKeys.EMAIL_USER_ID);
      email = guestEmail ? guestEmail : '';
    }
    return email;
  }
  getMarketingId() {
    return this.localStorage.getItem(CacheKeys.MARKETING_ID);
  }
  /**
   * If guest already authorized and logged in to the account and we got his email
   * @returns {boolean}
   */
  isAuth() {
    return this.isLoggedIn() && !isEmpty(this.getOAuthGuestID());
  }
  /**
   * Get source id from sourceIds Object
   * @param  {string} sourceIdKey Key passed that is matched with the sourceIds object.
   * @param  {string} [language=this.localeService.getFromStorage().language] get sourceId based on language
   * @return string returned value based on key
   */
  getSourceId(sourceIdKey, language = '') {
    if (language === '') {
      language = this.localeService.getFromStorage().language;
    }
    return get(this.tridionSite === 'ush' ? USHSourceIds : SourceIds, language + '.' + sourceIdKey);
  }
  /**
   * Get user_id of user
   * @return string
   */
  getUserId() {
    return this.getLoginUserName();
  }
  /**
   * Check if user is logged in
   * @return boolean
   */
  isLoggedIn() {
    return this.localStorage.getItem(CacheKeys.IS_SIGN_IN, true);
  }
  /**
   * Generate string
   * @param {number} length
   * @returns {string}
   */
  generateString(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*_';
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  }
  /**
   * Get set Etag from previous call.
   * @return string
   */
  getEtag() {
    return this.localStorage.getItem(CacheKeys.GUESTS_E_TAG) || '';
  }
  /**
   * Get ldap_id of user
   * @return string
   */
  getldapId() {
    return this.localStorage.getItem(CacheKeys.USER_L_DAP) || '';
  }
  /**
   * Clear all guests related info
   * @return {void}
   */
  clearGuest(oAuth = false) {
    const user = this.getLoginUserName();
    if (oAuth) {
      this.localStorage.removeItem(CacheKeys.OAUTH_LOGIN);
      this.localStorage.removeItem(CacheKeys.OAUTH_USER_NAME);
      this.localStorage.removeItem(CacheKeys.OAUTH_LOGIN_GID);
    }
    this.localStorage.setItem(CacheKeys.IS_SIGN_IN, 'false');
    this.localStorage.removeItem(CacheKeys.EMAIL_USER_ID);
    this.localStorage.removeItem(CacheKeys.USER_F_NAME);
    this.localStorage.removeItem(CacheKeys.USER_L_NAME);
    this.localStorage.removeItem(CacheKeys.USER_L_DAP);
    this.localStorage.removeItem(CacheKeys.MARKETING_ID);
    this.localStorage.removeItem(CacheKeys.LOGGEDIN_GUEST_ID);
    this.localStorage.removeItem(CacheKeys.LOGGEDIN_USER_ID);
    this.localStorage.removeItem(CacheKeys.GUESTS_E_TAG);
    this.localStorage.removeItem(CacheKeys.InteractGuestId);
    this.localStorage.removeItem('sessionNumber');
    this.localStorage.removeItem('CART_ITEM_COUNT');
    this.sessionStorage.removeItem('CART_ITEM_COUNT');
    if (user) {
      const label = get(this.pageLabels, 'SU1', '{0} has been logged out.').replace('{0}', user);
      this.notification.show(label, 'success');
    }
  }
  clearAuthn() {
    this.localStorage.removeItem(CacheKeys.HEADERS_WC_TOKEN);
    this.localStorage.removeItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN);
  }
  /**
   * Get relative url
   * @param {string} url
   * @returns {string}
   */
  getRelativeUrl(url) {
    return url.replace('{storeId}', this.localStorage.getItem(CacheKeys.STORE_ID) || this.defaultValues.STORE_ID).replace('{guestId}', this.getGuestId()).replace('{loginId}', this.localStorage.getItem(CacheKeys.EMAIL_USER_ID) || '');
  }
  /**
   * Address to contact
   * @param {IAddress | any} addressInfo
   * @returns {ISetEmailCommerceAddressRequest}
   */
  addressToContact(addressInfo) {
    const type = (addressInfo.address_type || addressInfo.addressType) === AddressType.billing;
    const addrType = type ? this.defaultValues.BILLING_ADDR : this.defaultValues.SHIPPING_ADDR;
    const getAddrSubType = type ? 'B' : 'S';
    const contact = {
      firstName: addressInfo.first_name || addressInfo.firstName || '',
      lastName: addressInfo.last_name || addressInfo.lastName || '',
      phone1: addressInfo.phone_number || '',
      city: addressInfo.city || '',
      state: addressInfo.state || '',
      country: addressInfo.country || '',
      addressLine: [addressInfo.address_line1 || addressInfo.addressLine1 || ''],
      email1: addressInfo.email_address || addressInfo.email || '',
      xaddr_addressField1: addressInfo.address_id || addressInfo.addressId || this.defaultValues.SOURCE_NO_VALIDATION,
      primary: (addressInfo.primary === true).toString(),
      addressType: getAddrSubType,
      nickName: [addrType, getAddrSubType, Date.now()].join('_'),
      zipCode: addressInfo.postal_code || addressInfo.zipPostalCode || '',
      phone1Type: this.localStorage.getItem(CacheKeys.IS_SIGN_IN) === 'true' ? '0' : this.localStorage.getItem(CacheKeys.PHONE_1_TYPE) || '1',
      sourceId: ''
    };
    contact.addressLine.push(addressInfo.address_line2 || addressInfo.addressLine2 || '');
    return contact;
  }
  /**
   * Replace by key
   * @param {any} obj
   * @param {string} path
   * @param {any} value
   * @returns {object|undefined}
   */
  replaceByPath(obj, path, value) {
    let schema = obj && typeof obj === 'object' ? obj : undefined;
    if (schema) {
      const pList = path.split('.');
      const len = pList.length;
      for (let i = 0; i < len - 1; i++) {
        const elem = pList[i];
        if (!schema[elem]) {
          schema[elem] = {};
        }
        schema = schema[elem];
      }
      schema[pList[len - 1]] = value;
    }
    return schema;
  }
  updateLoggedInStatus(bool) {
    const val = this._loggedInStatus.getValue();
    if (val !== bool || this.loggedInStatusChanged) {
      this.loggedInStatusChanged = false;
      this._loggedInStatus.next(bool);
    }
  }
  loginPreCheck() {
    const val = this._loggedInStatus.getValue();
    if (this.isLoggedIn() !== val) {
      // This happens if a user has a second tab open and has logged out on that tab
      // But other tab doesn't know about this. Next action should resolve status on second tab if not updated yet.
      this.loggedInStatusChanged = true;
    }
  }
  updateInstanceLoaded(bool) {
    this._instanceLoaded.next(bool);
  }
  static {
    this.ɵfac = function MsGuestBaseHelper_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsGuestBaseHelper)(i0.ɵɵinject(i1.PageLabelsResolver), i0.ɵɵinject(i2.LocaleService), i0.ɵɵinject(i3.AppEnvironmentService), i0.ɵɵinject(i4.LocalStorageService), i0.ɵɵinject(i4.SessionStorageService), i0.ɵɵinject(i5.SpinnerService), i0.ɵɵinject(i6.NotificationService), i0.ɵɵinject('DefaultValues'), i0.ɵɵinject('AppDefaults'), i0.ɵɵinject('RelativeUrls'), i0.ɵɵinject('TridionSite'));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MsGuestBaseHelper,
      factory: MsGuestBaseHelper.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsGuestBaseHelper, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.PageLabelsResolver
  }, {
    type: i2.LocaleService
  }, {
    type: i3.AppEnvironmentService
  }, {
    type: i4.LocalStorageService
  }, {
    type: i4.SessionStorageService
  }, {
    type: i5.SpinnerService
  }, {
    type: i6.NotificationService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['DefaultValues']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['RelativeUrls']
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['TridionSite']
    }]
  }], null);
})();

/**
 * Spinner Keys
 * @type {{}}
 */
const SpinnerKeys = {
  ACCOUNT_ENROLL: 'AccountEnroll',
  ORDER_PROFILE_TO_ACCOUNT: 'orderProfileToAccount',
  PROFILE_TO_ACCOUNT: 'profileToAccount',
  OVERRIDE: 'override',
  LOGIN: 'login',
  EMAIL_OPT_IN_OUT: 'guestEmailOptInOut',
  FORGOT_PASSWORD: 'forgotPassword',
  GET_BY_GUEST_ID: 'getByGuestId',
  USER_ID_UPDATE: 'UserIdUpdate',
  CHANGE_PASSWORD: 'changePassword',
  UPDATE_PERSONAL_ADDRESS: 'updatePersonalAddress',
  DELETE_PERSONAL_ADDRESS: 'deletePersonalAddress',
  UPDATE_PERSONAL_CONTACT_INFO: 'updatePersonalContactInfo',
  UPDATE_PREFERENCES: 'updatePreferences',
  UPDATE_USER_PREFERENCES: 'UPDATE_USER_PREFERENCES',
  UPDATE_USER_PREFERENCES_BY_EMAIL_ID: 'UPDATE_USER_PREFERENCES_BY_EMAIL_ID',
  LOAD_CARDS: 'loadCards',
  SET_CARD_PIN: 'setCardPin',
  ADD_PAYMENT_CARD: 'addPaymentCard',
  DELETE_PAYMENT_CARD: 'deletePaymentCard',
  SET_SPENDING_LIMIT: 'setSpendingLimit',
  GET_WALLET: 'getWallet',
  CREATE_GUEST_CONTACT: 'CreateGuestContact',
  COMMERCE_ADDRESS_REQUEST: 'submitCommerceAddressRequest',
  GET_PRIMARY_ADDRESS: 'getPrimaryAddress',
  SAVE_CARD_TO_ACC: 'SAVE_CARD_TO_ACC',
  MODIFY_USER_ADDRESS: 'MODIFY_USER_ADDRESS',
  GET_ACCOUNT_AVAILABLE: 'GET_ACCOUNT_AVAILABLE',
  CHECK_ACCOUNT_AVAILABLE: 'CHECK_ACCOUNT_AVAILABLE',
  SET_COMMERCE_ADDRESS_REQUEST: 'SET_COMMERCE_ADDRESS_REQUEST',
  CREATE_ASSIGNEE: 'CREATE_ASSIGNEE',
  UPDATE_USER_CONTACT: 'UPDATE_USER_CONTACT',
  GET_ENTITLEMENT_DETAILS: 'GET_ENTITLEMENT_DETAILS',
  RESET_PASSWORD: 'RESET_PASSWORD',
  CREATE_USER_ACCOUNT: 'CREATE_USER_ACCOUNT',
  LINK_ENTITLEMENT: 'LINK_ENTITLEMENT',
  GET_LINK_ENTITLEMENT: 'GET_LINK_ENTITLEMENT',
  ENTITLEMENTS_LINK_SEARCH_LINK: 'ENTITLEMENTS_LINK_SEARCH_LINK',
  GET_ENTITLEMENT_ASSIGNEES: 'GET_ENTITLEMENT_ASSIGNEES',
  VALIDATE_EMAIL: 'VALIDATE_EMAIL',
  ON_SUBMIT: 'ON_SUBMIT',
  GET_TRANSACTION_HISTORY: 'GET_TRANSACTION_HISTORY',
  SET_PRIMARY_PAYMENT_CARD: 'SET_PRIMARY_PAYMENT_CARD',
  GET_TICKETS: 'getTickets',
  GET_TRAVEL_PARTY_MEMBERS: 'GET_TRAVEL_PARTY_MEMBERS',
  SET_TRAVEL_PARTY_MEMBERS: 'SET_TRAVEL_PARTY_MEMBERS',
  UPDATE_TRAVEL_PARTY_MEMBERS: 'UPDATE_TRAVEL_PARTY_MEMBERS',
  HAS_PIN: 'HAS_PIN',
  GET_PAYMENT_CARD: 'GET_PAYMENT_CARD',
  UNENROLL_ACCOUNT: 'UNENROLL_ACCOUNT'
};
class MsGuestsBaseService {
  /**
   * Constructor
   * @param {DalService} dal
   * @param {MsGuestBaseHelper} helper
   */
  constructor(dal, helper) {
    this.dal = dal;
    this.helper = helper;
    /**
     * Request type: POST
     * @type {string}
     */
    this.TYPE_POST = 'POST';
    /**
     * Request type: DELETE
     * @type {string}
     */
    this.TYPE_DELETE = 'DELETE';
    /**
     * Request type: PUT
     * @type {string}
     */
    this.TYPE_PUT = 'PUT';
    /**
     * Request type: PATCH
     * @type {string}
     */
    this.TYPE_PATCH = 'PATCH';
    /**
     * Request type: GET
     * @type {string}
     */
    this.TYPE_GET = 'GET';
    /**
     * Define guests urls
     * @type {any}
     */
    this.urls = GuestsUrls;
    this.cacheClear = false;
  }
  /**
   * Do request to the endpoint
   * @param {string} url
   * @param {string|boolean} type
   * @param {any} request
   * @param {object} headers
   * @param {number} timeout
   * @param {boolean} retry
   * @param {boolean} get_response_headers
   */
  doRequest(url, type, request, headers, timeout, retry, get_response_headers = false, use_secure_api = false) {
    const secure_url = this.helper.appDefaults.SECURE_URL;
    const instanceId = this.helper.getInstanceId();
    const checkHeaders = type === this.TYPE_PUT || type === this.TYPE_DELETE || type === this.TYPE_POST || !isEmpty(headers);
    let httpHeaders = new HttpHeaders();
    if (!checkHeaders && this.cacheClear) {
      headers = headers || {};
      Object.assign(headers, this.helper.getNoCacheHeaders());
    }
    if (typeof headers === 'object') {
      for (const [key, value] of Object.entries(headers)) {
        httpHeaders = httpHeaders.set(key, value);
      }
    }
    if (instanceId) {
      httpHeaders = httpHeaders.set('x-instance-id', instanceId);
    }
    return this.dal.httpRequest(url, type ? type : this.TYPE_POST, request, httpHeaders, timeout, retry, true, use_secure_api ? secure_url : '').then(response => {
      if (checkHeaders && response && response?.headers || type === this.TYPE_GET && response?.headers.get('etag')) {
        const etag = response.headers.get('etag');
        if (etag) {
          this.helper.setEtag(etag);
        }
      }
      this.cacheClear = checkHeaders && !this.cacheClear;
      if (type === this.TYPE_DELETE && response.status === 204) {
        return response;
      }
      return get_response_headers ? response : get(response, 'body', response);
    });
  }
  /**
   * Replace the key
   * @param {string} str
   * @param {ILooseObject} store
   * @returns {string}
   */
  replaceKey(str, store) {
    return Object.entries(store).reduce((initial, [key, value]) => {
      return initial.replace(`{${key}}`, value);
    }, str);
  }
  static {
    this.ɵfac = function MsGuestsBaseService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsGuestsBaseService)(i0.ɵɵinject(i1$1.DalService), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MsGuestsBaseService,
      factory: MsGuestsBaseService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsGuestsBaseService, [{
    type: Injectable
  }], () => [{
    type: i1$1.DalService
  }, {
    type: MsGuestBaseHelper
  }], null);
})();

/**
 * Export AccountMsGuestsService
 */
class ProfileMsGuestsService extends MsGuestsBaseService {
  /**
   * Get profile by guestId
   * @param {string} guest_id
   * @param {HttpHeaders | Record<string, string | string[]>} headers
   * @returns {Promise<HttpResponse<IGuestsResponse>>}
   */
  getByGuestId(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, {
      guest_id
    }), this.TYPE_GET, undefined, headers, undefined, undefined, true);
  }
  /**
   * Update profile by guest_id
   * @param {IGuestsUpdate} request
   * @param {string} guest_id
   * @param {HttpHeaders | Record<string, string | string[]>} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateByGuestId(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, {
      guest_id
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Delete profile by guest_id
   * @param {string} guest_id
   * @param {HttpHeaders | Record<string, string | string[]>} headers
   * @returns {Promise<IGenericResponse>}
   */
  deleteByGuestId(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.WITH_ID, {
      guest_id
    }), this.TYPE_PUT, undefined, headers);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵProfileMsGuestsService_BaseFactory;
      return function ProfileMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵProfileMsGuestsService_BaseFactory || (ɵProfileMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(ProfileMsGuestsService)))(__ngFactoryType__ || ProfileMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ProfileMsGuestsService,
      factory: ProfileMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProfileMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * Export AccountMsGuestsComponent
 */
class ProfileMsGuestsClass {
  /**
   * Constructor
   * @param {ProfileMsGuestsService} service
   * @param {MsGuestBaseHelper} helper
   */
  constructor(appDefaults, service, helper) {
    this.appDefaults = appDefaults;
    this.service = service;
    this.helper = helper;
  }
  /**
   * Delete Profile by guest_id
   * @param {string} guest_id
   * @param {string} etag
   * @param headers
   * @returns {Promise<IGenericResponse}
   */
  deleteByGuestId(guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !etag) {
      return this.helper.promise.reject('Missing guest_id, or etag: ', {
        guest_id,
        etag
      });
    }
    headers['If-Match'] = etag;
    return this.service.deleteByGuestId(guest_id, headers).then(response => {
      this.helper.clearGuest(true);
      this.helper.updateLoggedInStatus(false);
      return response;
    });
  }
  /**
   * Get user profile by guest_id
   * @param  {string} guest_id
   * @return Promise<HttpResponse<IGuestsResponse>>
   */
  getByGuestId(guest_id) {
    if (!this.helper.isAuth()) {
      return this.helper.promise.reject('Need oAuth login first');
    }
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id');
    }
    this.helper.spinner.show(SpinnerKeys.GET_BY_GUEST_ID);
    this.helper.loginPreCheck();
    return this.service.getByGuestId(guest_id, this.helper.getNoCacheHeaders()).then(response => {
      if (isBoolean(response)) {
        return this.helper.promise.reject('OAuth has expired');
      }
      if (isEmpty(response.body)) {
        return this.helper.promise.reject('No guest data found for: ' + guest_id + '. Data returned is: ', response);
      }
      if (response && response.headers) {
        const etag = response.headers.get('etag') || '';
        this.helper.setEtag(etag);
      }
      const receiveUpdates = this.appDefaults.RESORT_AREA_CODE === 'UOR' ? 'preferences.comm_prefs.email_offers_promos' : 'preferences.comm_prefs.email_offers_promos_ush';
      this.helper.localStorage.setItem(CacheKeys.IS_SIGN_IN, 'true');
      this.helper.localStorage.setItem(CacheKeys.MARKETING_ID, get(response.body, 'marketing_id', ''));
      this.helper.localStorage.setItem(CacheKeys.EMAIL_USER_ID, get(response.body, 'user_id', ''));
      this.helper.localStorage.setItem(CacheKeys.OAUTH_USER_NAME, get(response.body, 'user_id', ''));
      this.helper.localStorage.setItem(CacheKeys.USER_L_DAP, get(response.body, 'ldap_id', ''));
      this.helper.localStorage.setItem(CacheKeys.USER_F_NAME, get(response.body, 'personal_info.contacts[0].first_name', ''));
      this.helper.localStorage.setItem(CacheKeys.USER_L_NAME, get(response.body, 'personal_info.contacts[0].last_name', ''));
      this.helper.localStorage.setItem(CacheKeys.USER_SELECTED_COUNTRY, get(response.body, 'personal_info.addresses[0].country_code', ''));
      this.helper.localStorage.setItem(CacheKeys.RECEIVE_UPDATES, get(response.body, receiveUpdates));
      this.helper.localStorage.setItem(CacheKeys.LOGGEDIN_GUEST_ID, this.helper.getOAuthGuestID());
      this.helper.localStorage.setItem(CacheKeys.ZIP_CODE, get(response.body, 'guestProfile.contact.zipCode'));
      this.helper.updateLoggedInStatus(true);
      this.helper.spinner.hide(SpinnerKeys.GET_BY_GUEST_ID);
      return response.body;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Update Profile By GuestId
   * @param {IGuestsUpdate} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {string} etag
   * @param headers
   * @returns {Promise<IGenericResponse>}
   */
  updateByGuestId(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !sourceId || !etag) {
      return this.helper.promise.reject('Missing guest_id, sourceId, or etag: ', {
        guest_id,
        sourceId,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    return this.service.updateByGuestId(request, guest_id, headers);
  }
  static {
    this.ɵfac = function ProfileMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ProfileMsGuestsClass)(i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(ProfileMsGuestsService), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ProfileMsGuestsClass,
      factory: ProfileMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProfileMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: ProfileMsGuestsService
  }, {
    type: MsGuestBaseHelper
  }], null);
})();
class UUIDGenerator {
  /**
   * Generate a random UUID
   * @returns Object with instance_id
   */
  static generateUUID() {
    const instance_id = v4();
    const instanceHash = instance_id.replace(/-/g, '');
    return {
      instance_id,
      instanceHash
    };
  }
  /**
   * Generate an instance ID and instanceHash (Mimicking Backend)
   * @param plain - Optional input string to be hashed into a UUID-like format
   * @returns Object with instance_id (UUID format) and instanceHash (UUID without hyphens)
   */
  static async generateInstance(plain) {
    if (plain) {
      const encoder = new TextEncoder();
      const data = encoder.encode(plain);
      const hashBuffer = await crypto.subtle.digest('SHA-512', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const instanceHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 32);
      // Format into UUID structure
      const hashAsArray = instanceHash.split('');
      hashAsArray.splice(20, 0, '-');
      hashAsArray.splice(16, 0, '-');
      hashAsArray.splice(12, 0, '-');
      hashAsArray.splice(8, 0, '-');
      const instance_id = hashAsArray.join('');
      return {
        instance_id,
        instanceHash
      };
    } else {
      // Generate a random UUID
      return UUIDGenerator.generateUUID();
    }
  }
}

/**
 * Export AccountMsGuestsService
 */
class AccountMsGuestsService extends MsGuestsBaseService {
  /**
   * Create account instance
   * @param {number} timeout
   * @param {boolean} retry
   */
  createInstance(headers, base_url_secondary, timeout, retry) {
    return this.doRequest(this.replaceKey(this.urls.INSTANCES.CREATE, {
      base_url_secondary
    }), this.TYPE_POST, undefined, headers, timeout, retry, true);
  }
  /**
   * Create contact
   * @param {ICreateContactRequest} request
   * @param {Record<string, any>} headers
   * @param {number} timeout
   * @param {boolean} retry
   * @returns {Promise<HttpResponse<ILooseObject>>}
   */
  createContact(request, headers, timeout, retry) {
    return this.doRequest(this.urls.CREATE_CONTACT, this.TYPE_POST, request, headers, timeout, retry, true);
  }
  createUnregistered(request) {
    return this.doRequest(this.urls.CREATE_UNREGISTERED, this.TYPE_PUT, request);
  }
  /**
   * Login
   * @param {string} user_id
   * @param {string} password
   * @param {string} redirectUrl
   * @param {string} sourceId
   */
  login(user_id, password, sourceId) {
    return this.dal.oAuthLogin(user_id, password, sourceId);
  }
  /**
   * Account enroll
   * @param {IAccountEnrollRequest} request
   * @param {Record<string, any>} headers
   * @returns {Promise<HttpResponse<IAccountEnrollResponse>>}
   */
  enroll(request, headers) {
    request.guests_request.personal_info.emails = [{
      email_address: request.creds.user_id,
      primary: true
    }];
    return this.doRequest(this.urls.ACCOUNT_ENROLL, this.TYPE_POST, request, headers, undefined, undefined, true);
  }
  /**
   * Delete guest account
   * @param {string} user_id
   * @returns {Promise<any>}
   */
  delete(user_id) {
    return this.doRequest(this.replaceKey(this.urls.ACCOUNT_DELETE, {
      user_id
    }), this.TYPE_DELETE).then(response => {
      return response;
    });
  }
  /**
   * Convert order profile to regular account
   * @param {IOrderProfileToAccountRequest} request
   * @param {Record<string, any>} headers
   * @returns {Promise<IAccountEnrollResponse>}
   */
  orderProfileToAccount(request, headers) {
    return this.doRequest(this.urls.ORDER_PROFILE_TO_ACCOUNT, this.TYPE_POST, request, headers);
  }
  /**
   * Enhance a guest profile, directly into an account
   * @param {IProfileToAccountRequest} request
   * @param {Record<string, any>} headers
   * @returns {Promise<IAccountEnrollResponse>}
   */
  profileToAccount(request, headers) {
    return this.doRequest(this.urls.PROFILE_TO_ACCOUNT, this.TYPE_POST, request, headers);
  }
  /**
   * Old guest order to profile.
   * @param  {*} request
   * @param  {Record<string, any>} headers
   * @return Promise<any>
   */
  createLinkedAccount(request, headers = {}) {
    return this.doRequest(this.urls.CREATE_LINKED_ACCOUNT, this.TYPE_POST, request, headers);
  }
  /**
   * Email opt in out
   * @param {IEmailOptInOutRequest} request
   * @param {Record<string, any>} headers
   * @param {number} timeout
   * @param {boolean} retry
   * @returns {Promise<ILooseObject>}
   */
  emailOptInOut(request, headers, timeout, retry) {
    return this.doRequest(this.urls.EMAIL_OPT_IN_OUT, this.TYPE_POST, request, headers, timeout, retry, true);
  }
  /**
   * Validate Email
   * @param {IValidateEmailRequest} request
   * @param {number} timeout
   */
  validateEmail(request, timeout) {
    return this.doRequest(this.urls.VALIDATE_EMAIL, this.TYPE_POST, request, undefined, timeout);
  }
  checkAccountAvailable(email_address) {
    return this.doRequest(this.urls.ACCOUNT_AVAILABLE, this.TYPE_POST, {
      email_address
    });
  }
  /**
   * Forgot password
   * @param {string} user_id
   * @returns {Promise<IGenericResponse>}
   */
  forgotPassword(user_id) {
    const sourceId = this.helper.getSourceId('WebForgotPassword');
    const headers = {
      'x-source-id': sourceId
    };
    return this.doRequest(this.replaceKey(this.urls.AUTH.FORGOT_PASSWORD, {
      user_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Reset password
   * @param {IResetPasswordRequest} request
   * @returns {Promise<IGenericResponse>}
   */
  resetPassword(request) {
    const sourceId = this.helper.getSourceId('WebPasswordReset');
    const headers = {
      'x-source-id': sourceId
    };
    return this.doRequest(this.replaceKey(this.urls.AUTH.RESET_PASSWORD, {
      user_id: request.loginId,
      request_id: request.requestId
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Change password
   * @param {IChangePasswordRequest} request
   * @param {string} user_id
   * @returns {Promise<HttpResponse<IGenericResponse>>}
   */
  changePassword(request, user_id) {
    const sourceId = this.helper.getSourceId('WebPasswordUpdate');
    const headers = {
      'x-source-id': sourceId
    };
    return this.doRequest(this.replaceKey(this.urls.AUTH.CHANGE_PASSWORD, {
      user_id
    }), this.TYPE_PUT, request, headers, undefined, undefined, true);
  }
  /**
   * Logout from user account
   * @returns {Promise<string>}
   */
  logout() {
    return this.dal.oAuthLogout(true).then(() => true).catch(() => false);
  }
  /**
   * Auth
   * @param {string} externalGuestId
   * @param {string} user_id
   * @param {string} ldap_id
   * @returns {Promise<ICommerceToken>}
   */
  authN(externalGuestId) {
    return this.doRequest(this.urls.COMMERCE.AUTHN, this.TYPE_POST, {
      externalGuestId
    }, {}, undefined, undefined, true).then(resp => resp.body || resp);
  }
  /**
   * Load contact ID
   * @param {string} store_id
   * @returns {Promise<IContract>}
   */
  loadContractId(store_id) {
    return this.doRequest(this.replaceKey(this.urls.CONTRACT, {
      store_id
    }), this.TYPE_GET);
  }
  /**
   * Update by user_id
   * @param {IUserIdUpdate} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateByUserId(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.USER_ID_UPDATE, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
  Unenroll a specific guest account based on new iOS account delete specifications
  * @param {string} guest_id
  * @param {string} sourceId
  */
  unenrollAccount(guest_id, sourceId) {
    if (!sourceId) {
      sourceId = this.helper.getSourceId('WebAccountDelete');
    }
    const headers = {
      'x-source-id': sourceId
    };
    return this.doRequest(this.replaceKey(this.urls.UNENROLL_ACCOUNT, {
      guest_id
    }), this.TYPE_POST, undefined, headers);
  }
  getAffiliations(guest_id, headers, active, ticket_id) {
    const params = pickBy({
      active,
      ticket_id
    }, negate(isUndefined));
    let urlParams = new URLSearchParams(Object.entries(params)).toString();
    if (urlParams) {
      urlParams = `?${urlParams}`;
    }
    const url = this.replaceKey(this.urls.GUESTS.AFFILIATIONS, {
      guest_id
    }) + urlParams;
    return this.doRequest(url, this.TYPE_GET, undefined, headers);
  }
  getTravelPartyAffiliations(guest_id, headers, active) {
    const params = pickBy({
      active
    }, negate(isUndefined));
    let urlParams = new URLSearchParams(Object.entries(params)).toString();
    if (urlParams) {
      urlParams = `?${urlParams}`;
    }
    const url = this.replaceKey(this.urls.GUESTS.TRAVEL_PARTY_AFFILIATIONS, {
      guest_id
    }) + urlParams;
    return this.doRequest(url, this.TYPE_GET, undefined, headers);
  }
  getAccountAffiliations(guest_id, headers, active) {
    const params = pickBy({
      active
    }, negate(isUndefined));
    let urlParams = new URLSearchParams(Object.entries(params)).toString();
    if (urlParams) {
      urlParams = `?${urlParams}`;
    }
    const url = this.replaceKey(this.urls.GUESTS.ACCOUNT_AFFILIATIONS, {
      guest_id
    }) + urlParams;
    return this.doRequest(url, this.TYPE_GET, undefined, headers);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵAccountMsGuestsService_BaseFactory;
      return function AccountMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵAccountMsGuestsService_BaseFactory || (ɵAccountMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(AccountMsGuestsService)))(__ngFactoryType__ || AccountMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AccountMsGuestsService,
      factory: AccountMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AccountMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

/**
 * Export AccountMsGuestsComponent
 */
class AccountMsGuestsClass {
  /**
   * ConstructorF
   * @param {AccountMsGuestsService} service
   * @param {ProfileMsGuestsClass} profileClass
   * @param {MsGuestBaseHelper} helper
   */
  constructor(appDefaults, service, profileClass, helper, zone, dal) {
    this.appDefaults = appDefaults;
    this.service = service;
    this.profileClass = profileClass;
    this.helper = helper;
    this.zone = zone;
    this.dal = dal;
    this.subscriptions$ = new Subscription();
    this.dal.appTokenSet$.pipe(filter(data => data === TokenStatus.Refresh), scan((acc, _) => ({
      count: acc.count + 1
    }), {
      count: 0
    })).subscribe(({
      count
    }) => {
      // Always send event
      sendEvent('udx.ms_guests.logged_in_status', {
        logged_in: this.helper.isAuth(),
        guest_id: this.helper.getGuestId()
      });
      // Only run instance logic once (on first time)
      if (count === 1) {
        if (!this.helper.envConfig.skip_instance) {
          if (!this.helper.getInstanceId()) {
            this.createGuestInstance();
            return;
          }
          this.helper.updateInstanceLoaded(true);
        } else {
          this.helper.updateInstanceLoaded(false);
        }
      }
    });
    this.subscriptions$.add(this.dal.appTokenSet$.pipe(filter(data => data === TokenStatus.Reset || data === TokenStatus.Reset_Authn)).subscribe(data => {
      if (data === TokenStatus.Reset) {
        this.helper.clearGuest(true);
        this.helper.clearAuthn();
        this.helper.updateLoggedInStatus(false);
      } else if (data === TokenStatus.Reset_Authn) {
        // Always send event after login
        sendEvent('udx.ms_guests.logged_in_status', {
          logged_in: this.helper.isAuth(),
          guest_id: this.helper.getGuestId()
        });
        this.helper.clearAuthn();
      }
    }));
  }
  /**
   * Get and set UUID instance for the app.
   * @return (Promise<HttpResponse<ILooseObject> | void>) Returns response. But sets instance id and etag
   */
  async createGuestInstance() {
    if (this.helper.getInstanceId()) {
      return this.helper.promise.resolve();
    }
    if (this.helper.envConfig.skip_instance) {
      this.helper.updateInstanceLoaded(false);
      return this.helper.catchError('Skipping instance creation');
    }
    // Use locally generated UUID if enabled.
    // After ms-instance deprecation, the service fallback will be removed
    // once all web apps have migrated to and tested local instance ID generation.
    if (this.helper.envConfig.generate_local) {
      try {
        const {
          instance_id
        } = await UUIDGenerator.generateInstance();
        this.helper.setInstanceId(instance_id);
        this.helper.updateInstanceLoaded(true);
        return this.helper.promise.resolve();
      } catch (error) {
        this.helper.updateInstanceLoaded(false);
        return this.helper.catchError('Failed to generate local Instance id');
      }
    }
    return this.service.createInstance(this.helper.getNoCacheHeaders(), this.dal.envConfig.base_url_secondary).then(response => this.helper.checkGuestHeaders(response)).then(response => {
      const instanceId = response.headers.get('location');
      const eTag = response.headers.get('etag');
      if (!instanceId || !eTag) {
        return this.helper.promise.reject('Failed to retrieve correct headers.', {
          instanceId,
          eTag
        });
      }
      this.helper.setInstanceId(instanceId);
      this.helper.setEtag(eTag);
      this.helper.updateInstanceLoaded(true);
      return response;
    }, error => {
      this.helper.updateInstanceLoaded(false);
      this.helper.catchError(error);
    });
  }
  /**
   * Create guest based on interface.
   * @param  {ICreateContactRequest} [request] Use interface to fill out the request
   * @param  {string} [sourceId] the sourceID of the resource being requested
   * @param  {boolean} [skipAccountCheck] Whether or not to skip accountsAvailable call. Default is false
   * @param  {string} [instanceId=this.getInstanceId()] The instance id - UUID
   * @param  {HttpHeaders} [headers=this.noCacheHeaders] additional headers to be added
   * @param  {number} [timeout] Amount of time to wait
   * @param  {boolean} [retry] How many times to retry
   * @return (Promise<ILooseObject | void>)  Return response from server, otherwise reject.
   */
  createGuestContact(request, sourceId, skipAccountCheck = false, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders(), timeout, retry) {
    if (!instanceId) {
      return this.helper.promise.reject('Missing instanceId: ', instanceId);
    }
    headers['x-source-id'] = sourceId;
    this.helper.spinner.show(SpinnerKeys.CREATE_GUEST_CONTACT);
    const email = request.user_id || get(request.personal_info, 'emails[0].email_address', '');
    const getResponse = skipAccountCheck ? this.validateEmail(email) : this.checkAccountAvailable(email).then(() => this.validateEmail(email));
    return getResponse.then(() => {
      if (!request.personal_info.emails) {
        request.personal_info.emails = [{
          email_address: email,
          primary: true
        }];
      }
      delete request.user_id;
      return this.service.createContact(request, headers, timeout, retry).then(response2 => {
        this.helper.spinner.hide(SpinnerKeys.CREATE_GUEST_CONTACT);
        const headerLocation = response2.headers.get('location');
        this.helper.localStorage.setItem(CacheKeys.GUEST_LOCATION_ID, headerLocation);
        return response2;
      }, error => this.helper.catchError(error));
    }).catch(error => {
      this.helper.spinner.show(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  // Please remove once updates come to new guests
  createUnregistered(request) {
    return this.service.createUnregistered(request).then(status => {
      if (status.statusCode === 200) {
        this.helper.showLabel('SU12', 'success');
      }
      return status;
    }).catch(error => {
      const errorMessage = get(error, 'error.message', this.helper.pageLabels[error]);
      this.helper.showLabel(errorMessage);
      return this.helper.promise.reject(errorMessage);
    });
  }
  /**
   * Enroll account to UPR
   * @param  {IAccountEnrollRequest} request
   * @param  {string} [sourceId] the sourceID of the resource being requested
   * @param  {string} [instanceId=this.getInstanceId()] The instance id - UUID
   * @param  {Record<string, any>} [headers=this.noCacheHeaders] additional headers to be added
   * @return (Promise<HttpResponse<IAccountEnrollResponse>>) Get response or returned error string
   */
  enroll(request, sourceId, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders()) {
    if (!instanceId) {
      return this.helper.promise.reject('Missing instanceId: ', instanceId);
    }
    headers['x-instance-id'] = instanceId;
    headers['x-source-id'] = sourceId;
    this.helper.spinner.show(SpinnerKeys.ACCOUNT_ENROLL);
    return this.service.enroll(request, headers).then(response => {
      this.helper.spinner.hide(SpinnerKeys.ACCOUNT_ENROLL);
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU2', 'success');
      return response;
    }).catch(error => {
      let errorMessage = 'ER71';
      const message = get(error, 'error.message', error);
      if (error.status === 409 || message && message.indexOf('Conflict') > -1) {
        errorMessage = 'ER20';
      }
      this.helper.showLabel(errorMessage);
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Delete account
   * @param {string} user_id
   * @returns {Promise<boolean>}
   */
  deleteAccount(user_id = this.helper.getUserId()) {
    return this.service.delete(user_id).then(response => {
      this.reset();
      return response.status === 204;
    });
  }
  /**
   * Unenroll Account
   * @param {string} guest_id
   * @param {string} sourceId
   * @returns {Promise<any>}
   */
  unenrollAccount(guest_id = this.helper.getGuestId(), sourceId = this.helper.getSourceId('WebAccountDelete')) {
    this.helper.spinner.show(SpinnerKeys.UNENROLL_ACCOUNT);
    return this.service.unenrollAccount(guest_id, sourceId).then(response => {
      this.helper.spinner.hide(SpinnerKeys.UNENROLL_ACCOUNT);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71', 'error');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Upgrade guest account to profile.
   * @param  {IOrderProfileToAccountRequest} request
   * @param  {string} [sourceId] the sourceID of the resource being requested
   * @param  {HttpHeaders} [headers]
   * @param  {number} [retry]
   * @return Promise<IAccountEnrollResponse | string> Get response or returned error string
   */
  orderProfileToAccount(request, sourceId, redirectUrl, bypassAuth = false, headers = this.helper.getNoCacheHeaders(), retry = 3) {
    headers['x-source-id'] = sourceId;
    this.helper.spinner.show(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
    return this.service.orderProfileToAccount(request, headers).catch(error => this.handleAccountCreationError(error)).then(response => {
      if (!get(response, 'guest_id')) {
        const error = get(response, 'message', '');
        this.helper.showLabel(error);
        this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return this.helper.promise.reject(error);
      }
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU2', 'success');
      return this.login(request.user_id, request.password).then(guestData => {
        this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return guestData;
      });
    }).then(() => {
      if (!bypassAuth) {
        return this.authN();
      }
      return;
    }).catch(error => {
      if (error === 'ER71' && retry === 0) {
        this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
        this.helper.showLabel(error);
        return this.helper.promise.reject(error);
      }
      return new Promise(resolve => {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => {
            resolve(this.orderProfileToAccount(request, sourceId, redirectUrl, bypassAuth, headers, retry - 1));
          }, 5000);
        });
      });
    });
  }
  /**
   * Enhance a guest profile, directly into an account
   * @param  {string} [email]
   * @param  {IProfileToAccountRequest} [request]
   * @param  {string} [sourceId] the sourceID of the resource being requested
   * @param  {string} [redirectUrl] redirect Url for the user account
   * @param  {string} [signUpLocation] redirect Url for the user account
   * @param  {HttpHeaders} [headers]
   * @return Promise<IGuestsCollection | string> Get response or returned error string
   */
  profileToAccount(email, request, sourceId, signUpLocation = 'register-account', headers = this.helper.getNoCacheHeaders()) {
    headers['x-source-id'] = sourceId;
    this.helper.spinner.show(SpinnerKeys.PROFILE_TO_ACCOUNT);
    return this.service.profileToAccount(request, headers).catch(error => signUpLocation === 'package-create-account' ? this.handlePackageCreateAccountError(error) : this.handleRegisterAccountError(error)).then(response => {
      if (!get(response, 'guest_id')) {
        const error = get(response, 'message', '');
        this.helper.showLabel(error);
        this.helper.spinner.hide(SpinnerKeys.PROFILE_TO_ACCOUNT);
        return this.helper.promise.reject(error);
      }
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU2', 'success');
      return this.login(email, request.password);
    }).then(guestData => {
      this.helper.spinner.hide(SpinnerKeys.PROFILE_TO_ACCOUNT);
      return guestData;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      this.helper.showLabel(error);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Old guest order to profile.
   * @param  {*} request
   * @return Promise<any>
   */
  createLinkedAccount(request, sourceId, redirectUrl, headers = this.helper.getNoCacheHeaders(), retry = 3) {
    const oldRequest = {
      sourceId: sourceId,
      customerOrderId: request.customer_order_id,
      guestId: this.helper.getInstanceId(),
      userId: request.user_id,
      password: request.password
    };
    this.helper.spinner.show(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
    return this.service.createLinkedAccount(oldRequest, headers).catch(error => this.handleAccountCreationError(error)).then(response => {
      if (!get(response, 'result.guestProfile')) {
        const error = get(response, 'message');
        this.helper.showLabel(error);
        this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return this.helper.promise.reject(error);
      }
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU2', 'success');
      return this.login(request.user_id, request.password).then(guestData => {
        this.helper.spinner.hide(SpinnerKeys.ORDER_PROFILE_TO_ACCOUNT);
        return guestData;
      });
    }).then(() => this.authN()).catch(error => {
      if (error === 'ER71' && retry === 0) {
        this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
        this.helper.showLabel(error);
        return this.helper.promise.reject(error);
      }
      return new Promise(resolve => {
        this.zone.runOutsideAngular(() => {
          setTimeout(() => {
            resolve(this.createLinkedAccount(request, sourceId, redirectUrl, headers, retry - 1));
          }, 5000);
        });
      });
    });
  }
  /**
   * Login user. Email (user_id) and password required.
   * @param  {string} username this is the email of the user account
   * @param  {string} password the password for the user account
   * @param  {string} redirectUrl redirect Url for the user account
   * @param  {boolean} activateAuthN Do the authn call, default false.
   * @return (Promise<string | void>) returns a Contract-ID
   */
  login(username, password, activateAuthN = false, sourceId) {
    if (!username || !password) {
      return this.helper.promise.reject('Username and Password are mandatory');
    }
    this.helper.spinner.show(SpinnerKeys.LOGIN);
    let userInfo = {};
    this.helper.loginPreCheck();
    return this.service.login(username, password, sourceId).catch(error => {
      let message = error && typeof error != 'object' ? error.toLowerCase() : '';
      switch (message) {
        case 'account locked':
          message = this.helper.showLabel('ER28');
          break;
        case 'invalid username':
          message = this.helper.showLabel('ER24');
          break;
        default:
          message = this.helper.showLabel('ErrorLoginLabel');
      }
      return this.helper.promise.reject(message);
    }).then(response => {
      if (response !== 'Login success') {
        return this.helper.promise.reject(response === 'invalid username' ? this.helper.showLabel('ER24') : this.helper.showLabel('ErrorLoginLabel'));
      }
      return;
    }).then(() => {
      this.helper.clearAuthn();
      if (activateAuthN) {
        return this.authN();
      }
      return;
    }).then(() => {
      const guest_id = this.helper.getGuestId();
      return this.profileClass.getByGuestId(guest_id);
    }).then(user => {
      if (user && user.user_id && user.ldap_id) {
        userInfo = user;
      }
      return;
    }).then(() => {
      this.helper.spinner.hide(SpinnerKeys.LOGIN);
      return userInfo;
    }).catch(error => {
      this.helper.showLabel(error ? error : 'ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    }).finally(() => {
      this.helper.updateLoggedInStatus(this.helper.isAuth());
    });
  }
  /**
   * Get and set contract_id for user
   * @return (Promise<string | void>) Returns the contractId based on store_id for user.
   */
  loadContractId() {
    if (!this.helper.envConfig.cart_enabled) {
      return Promise.resolve('');
    }
    if (!this.helper?.defaultValues?.STORE_ID) {
      return this.helper.promise.reject('Missing STORE_ID in injected provider: defaultValues');
    }
    const store_id = this.helper.defaultValues.STORE_ID;
    this.helper.localStorage.setItem(CacheKeys.STORE_ID, store_id);
    const contract_id = this.helper.localStorage.getItem(CacheKeys.CONTRACT_ID);
    if (this.helper.localStorage.getItem(CacheKeys.CONTRACT_ID)) {
      return Promise.resolve(contract_id);
    }
    return this.service.loadContractId(store_id).then(response => {
      if (isEmpty(response)) {
        return this.helper.promise.reject('Missing data from loadContractId: ', response);
      }
      const contract_id = values(get(response, 'contracts'))[0];
      this.helper.localStorage.setItem(CacheKeys.CONTRACT_ID, contract_id);
      return contract_id;
    });
  }
  /**
   * Gets and sets store tokens.
   * @param {string} [instance_id=this.getInstanceId()]
   * @return Promise<ICommerceToken> ICommerceToken response
   */
  async authN(instance_id = this.helper.getInstanceId()) {
    if (!this.helper.envConfig.cart_enabled) {
      return Promise.resolve();
    }
    if (this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TOKEN)) {
      return this.loadContractId();
    }
    const retryStr = genericRetryStrategy();
    return firstValueFrom(from(this.createGuestInstance()).pipe(switchMap(() => this.service.authN(instance_id || this.helper.getInstanceId())), switchMap(response => {
      if (isEmpty(response)) {
        return this.helper.promise.reject('Missing data from authN: ', response);
      }
      this.helper.localStorage.setItem(CacheKeys.HEADERS_WC_TOKEN, get(response, 'result.WCToken'), {
        duration: {
          minutes: 30
        }
      });
      this.helper.localStorage.setItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN, get(response, 'result.WCTrustedToken'), {
        duration: {
          minutes: 30
        }
      });
      return from([response]); // Wrap response in an observable
    }), switchMap(() => this.loadContractId()), retry(retryStr), catchError(errorResponse => {
      console.error(errorResponse);
      this.helper.clearAuthn();
      throw errorResponse;
    })));
  }
  /**
   * Subscribe user to email based on source_id
   * @param  {IEmailOptInOutRequest} [request] Use interface to fill out the request
   * @param  {string} [sourceId]  the sourceID of the resource being requested
   * @param  {string} [instanceId=this.getInstanceId()] The instance id - UUID
   * @param  {HttpHeaders} [headers=this.noCacheHeaders] additional headers to be added
   * @param  {number} [timeout] Amount of time to wait
   * @param  {boolean} [retry] How many times to retry
   * @return (Promise<ILooseObject | void>) Return response from server, otherwise reject.
   */
  guestEmailOptInOut(request, sourceId, instanceId = this.helper.getInstanceId(), headers = this.helper.getNoCacheHeaders(), timeout, retry) {
    if (!instanceId) {
      return this.helper.promise.reject('Missing instanceId: ', instanceId);
    }
    this.helper.spinner.show(SpinnerKeys.EMAIL_OPT_IN_OUT);
    headers['x-instance-id'] = instanceId;
    headers['x-source-id'] = sourceId;
    return this.service.emailOptInOut(request, headers, timeout, retry).then(response => {
      const optIn = this.appDefaults.RESORT_NAME === 'UO' ? request.email_offers_promos : request.email_offers_promos_ush;
      if (optIn) {
        this.helper.showLabel('SU12', 'success');
      } else {
        this.helper.showLabel('SU11', 'success');
      }
      this.helper.spinner.hide(SpinnerKeys.EMAIL_OPT_IN_OUT);
      return response;
    }, error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Check if user email (user_id) is real or not. Stop call if service takes to long to get back.
   * @param  {string} email This is usually the user_id (email) of the user
   * @param  {number} [timeout] When to timeout the call
   * @return (Promise<string | void>)  Will return 'success' | 'ignore' | an error message
   */
  validateEmail(email, timeout) {
    const marketingId = get(this.helper.defaultValues, 'SOURCE_MARKETING_ID', undefined);
    if (!email || !marketingId) {
      return this.helper.promise.reject('Email or SOURCE_MARKETING_ID missing', {
        email,
        marketingId
      });
    }
    const timeoutDuration = timeout ? timeout : Number(get(this.helper.pageLabels, 'ValidateEmailTimeOut', 5000));
    const request = {
      email,
      marketingId
    };
    this.helper.spinner.show(SpinnerKeys.VALIDATE_EMAIL);
    return this.service.validateEmail(request, timeoutDuration).then(response => this.handleEmailValidation(response, email)).then(() => {
      this.helper.$validationTimeout.next(false);
      this.helper.spinner.hide(SpinnerKeys.VALIDATE_EMAIL);
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      const isTimeoutError = get(error, 'name', '') === 'TimeoutError';
      const currentTimeOutStatus = this.helper.$validationTimeout.getValue();
      if (isTimeoutError && currentTimeOutStatus && this.skipValidation(email)) {
        this.helper.$validationTimeout.next(false);
        return this.helper.promise.resolve('success');
      } else if (isTimeoutError && !currentTimeOutStatus) {
        this.skipValidation(email);
        this.helper.$validationTimeout.next(true);
        if (!!error && !!error.message) error = this.helper.pageLabels['ER71'];
      }
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Check the guest database and see if the email given is available
   * @param  {string} email Email to check database against.
   * @return Promise<any>
   */
  checkAccountAvailable(email) {
    if (!email) {
      return this.helper.promise.reject('Email Required');
    }
    this.helper.spinner.show(SpinnerKeys.CHECK_ACCOUNT_AVAILABLE);
    return this.service.checkAccountAvailable(email).then(response => {
      this.helper.spinner.hide(SpinnerKeys.CHECK_ACCOUNT_AVAILABLE);
      if (!response.is_account_available) {
        return this.helper.promise.reject(this.helper.showLabel('ER67'));
      }
      return response.is_account_available;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Check if email is already in use and enroll user if not in use. Email (user_id) required. Requires recaptcha.
   * @param  {IAccountEnrollRequest} request
   * @param  {string} sourceId the sourceID of the resource being requested
   * @param  {boolean} skipAccountCheck Whether or not to skip accountsAvailable call. Default is false
   * @param  {string} [instanceId=this.getInstanceId()] The instance id - UUID
   * @param  {HttpHeaders} [headers=this.noCacheHeaders] additional headers to be added
   * @param  {boolean} byPassOnTimeOut bypass email validation on a second timeout error and create an account.
   * @return (Promise<string | void>) will return 'emailInUse' if in use, otherwise returns validateEmail return.
   */
  getAccountAvailable(request, sourceId, skipAccountCheck = false, instanceId = this.helper.getInstanceId(), headers, byPassOnTimeOut) {
    if (!request.g_recaptcha_response) {
      return this.helper.promise.reject('Recaptcha Required');
    }
    if (!instanceId) {
      return this.helper.promise.reject('Missing instanceId: ', instanceId);
    }
    this.helper.spinner.show(SpinnerKeys.GET_ACCOUNT_AVAILABLE);
    const getResponse = skipAccountCheck ? this.validateEmail(request.creds.user_id) : this.checkAccountAvailable(request.creds.user_id).then(() => this.validateEmail(request.creds.user_id));
    return getResponse.then(response => {
      return this.goToEnroll(request, sourceId, instanceId, headers, response);
    }).catch(error => {
      const isTimeoutError = get(error, 'name', '') === 'TimeoutError';
      if (isTimeoutError && byPassOnTimeOut) {
        return this.goToEnroll(request, sourceId, instanceId, headers);
      }
      this.helper.spinner.show(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  goToEnroll(request, sourceId, instanceId, headers, response) {
    return this.enroll(request, sourceId, instanceId, headers).then(accountResponse => {
      this.helper.spinner.hide(SpinnerKeys.GET_ACCOUNT_AVAILABLE);
      if (get(accountResponse, 'status') === 409) {
        this.helper.showLabel('ER67');
        return 'emailInUse';
      }
      return response;
    });
  }
  /**
   * Start the process for user forget password
   * @param {string} user_id
   * @return (Promise<IGenericResponse | void>)
   */
  forgotPassword(user_id) {
    if (!user_id) {
      return this.helper.promise.reject('Needs a user email');
    }
    this.helper.spinner.show(SpinnerKeys.FORGOT_PASSWORD);
    return this.service.forgotPassword(user_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.FORGOT_PASSWORD);
      if (response === 'skip') {
        return;
      }
      const messageSent = get(response, 'message', '') === 'Email sent successfully';
      if (!messageSent) {
        return this.helper.showLabel('ER24');
      }
      this.helper.showLabel('SU3', 'success');
      return;
    }).catch(error => {
      const responseCode = error && error.status === 500 ? 'ER71' : 'ER24';
      this.helper.showLabel(responseCode);
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Complete the process of user reset password. This is usually done after a forgot password call.
   * @param  {IResetPasswordRequest} request
   * @return (Promise<IGenericResponse | void>)
   */
  resetPassword(request) {
    if (!request || !request.loginId || !request.requestId || !request.newPassword) {
      return this.helper.promise.reject('Please provide missing attributes for request; ', request);
    }
    this.helper.spinner.show(SpinnerKeys.RESET_PASSWORD);
    return this.service.resetPassword(request).then(res => {
      this.helper.spinner.hide(SpinnerKeys.RESET_PASSWORD);
      this.helper.showLabel('SU8', 'success');
      return res;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.handleError(error);
    });
  }
  /**
   * Change the users password.
   * @param {IChangePasswordRequest} request
   * @param {string} user_id
   * @return (Promise<HttpResponse<IGenericResponse> | void>)
   */
  changePassword(request, user_id = this.helper.getUserId()) {
    if (!request || !request.newPassword || !request.oldPassword || !user_id) {
      return this.helper.promise.reject('Please provide missing attributes for request for: ' + user_id + ' - ', request);
    }
    this.helper.spinner.show(SpinnerKeys.CHANGE_PASSWORD);
    return this.service.changePassword(request, user_id).then(response => {
      if (!response || response.status === 403) {
        return this.helper.catchError(this.helper.showLabel('ER71'));
      }
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'success');
      this.helper.showLabel('SU8', 'success');
      this.helper.spinner.hide(SpinnerKeys.CHANGE_PASSWORD);
      return response;
    }).catch(error => {
      let responseCode = 'ER71';
      if (error && (error.statusCode === 403 || error.status === 403)) {
        const message = get(error, 'message', '');
        if (message.indexOf('LDAP: error code 49') > -1 || message.indexOf('LDAP: error code 48') > -1) {
          responseCode = 'ER8';
        } else if (message.indexOf('LDAP: error code 53') > -1) {
          responseCode = 'ER28';
        }
      }
      this.helper.showLabel(responseCode);
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(responseCode);
    });
  }
  /**
   * Update by user_id
   * @param {IUserIdUpdate} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateByUserId(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    if (request.user_id !== request.user_id_confirm) {
      return this.helper.promise.reject('Email confirmation is the not same; ', request);
    }
    this.helper.spinner.show(SpinnerKeys.USER_ID_UPDATE);
    headers['x-source-id'] = sourceId;
    return this.service.updateByUserId(request, guest_id, headers).then(response => {
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, true);
      this.helper.localStorage.setItem(CacheKeys.EMAIL_CHANGED, true);
      this.helper.localStorage.setItem(CacheKeys.EMAIL_USER_ID, request.user_id);
      this.helper.localStorage.setItem(CacheKeys.OAUTH_USER_NAME, request.user_id);
      this.helper.showLabel('SU7', 'success');
      this.helper.spinner.hide(SpinnerKeys.USER_ID_UPDATE);
      return response;
    }).catch(error => {
      let errorMessage = 'ER71';
      if (error.status >= 400 && error.status <= 408) {
        errorMessage = 'ER84';
      }
      if (error.status === 409) {
        errorMessage = 'ER61';
      }
      this.helper.showLabel(errorMessage);
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Logout user.
   * @return Promise<boolean> Returns boolean
   */
  logout() {
    this.helper.loginPreCheck();
    return this.service.logout().then(async response => {
      if (response) {
        const userId = this.helper.getUserId();
        if (userId) {
          this.helper.showLabel(this.helper.findLabel('SU1').replace('{0}', userId), 'success');
        }
        try {
          return await this.reset();
        } catch (error) {
          throw error;
        }
      }
      return this.helper.promise.reject('Error on logout: ', response);
    }).finally(() => {
      this.helper.updateLoggedInStatus(this.helper.isAuth());
    });
  }
  getAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active, ticket_id) {
    if (!this.helper.isLoggedIn()) {
      return Promise.reject(false);
    }
    headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
    return this.service.getAffiliations(guest_id, headers, active, ticket_id).catch(error => this.helper.promise.reject(error));
  }
  getTravelPartyAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active) {
    if (!this.helper.isLoggedIn()) {
      return Promise.reject(false);
    }
    headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
    return this.service.getTravelPartyAffiliations(guest_id, headers, active).catch(error => this.helper.promise.reject(error));
  }
  getAccountAffiliations(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders(), active) {
    if (!this.helper.isLoggedIn()) {
      return Promise.reject(false);
    }
    headers['x-resort-area-code'] = get(this.appDefaults, 'RESORT_AREA_CODE');
    return this.service.getAccountAffiliations(guest_id, headers, active).catch(error => this.helper.promise.reject(error));
  }
  /**
   * Get the normalized version of the error.
   * @param  {*} response Response from email validation function
   * @param  {*} [override] bypass email check or not
   * @return {string} Return error..
   */
  normalizeEmailValidationResponse(response, override) {
    const result = get(response, 'result');
    if (!result || !result.verboseOutput) {
      return '';
    }
    let message = 'DEFAULT';
    const undeliverable = result.certainty === 'undeliverable';
    const overrideCheck = override && !undeliverable;
    switch (result.verboseOutput) {
      case 'unknown':
      case 'acceptAll':
      case 'verified':
      case 'timeout':
        message = 'A';
        break;
      case 'mailboxDisabled':
        message = overrideCheck ? 'A' : 'ER45';
        break;
      case 'mailboxDoesNotExist':
        message = overrideCheck ? 'A' : 'ER46';
        break;
      case 'mailboxFull':
        message = overrideCheck ? 'A' : 'ER47';
        break;
      case 'syntaxFailure':
        message = overrideCheck ? 'A' : 'ER48';
        break;
      case 'illegitimate':
      case 'unreachable':
      case 'unresolvable':
      case 'roleAccount':
      case 'typoDomain':
      case 'disposable':
        // overrideCheck
        message = overrideCheck ? 'A' : 'ER49';
        break;
    }
    return message;
  }
  /**
   * Show label if applicable and also return successful if email validation passes.
   * @param  {(IValidateEmailResponse | string)} response
   * @return (Promise<string | void>) return a string version of the error.
   */
  handleEmailValidation(response, email) {
    const responseType = !response || response === 'ignore' ? 'A' : this.normalizeEmailValidationResponse(response, this.helper.bypassEmailCheck);
    let error = this.helper.findLabel('ER6');
    switch (responseType) {
      case 'A':
        this.helper.bypassEmailCheck = false;
        return this.helper.promise.resolve('success');
      case 'ER45':
      case 'ER46':
      case 'ER47':
      case 'ER48':
      case 'ER49':
        this.helper.bypassEmailCheck = true;
        if (this.helper.$emailInValidation.getValue() != email) error = this.helper.showLabel(responseType);
        break;
      default:
        this.helper.bypassEmailCheck = false;
        if (responseType !== 'Default') {
          this.helper.showLabel(error);
        }
        break;
    }
    return !this.skipValidation(email) ? this.helper.promise.reject(error) : this.helper.promise.resolve('success');
  }
  handleError(error) {
    const statusCode = Number(get(error, 'statusCode', error.status));
    let errorCode = 'CONST_RESET_PASSWORD_MSG_ERROR';
    if (statusCode === 432) {
      errorCode = 'ER9';
    } else if (statusCode === 433 || statusCode === 436) {
      errorCode = 'ER86';
    } else if (statusCode === 434) {
      errorCode = 'ER60';
    } else if (statusCode === 435) {
      errorCode = 'ER51';
    } else if (statusCode === 400 && includes(error.message, 'has expired')) {
      this.helper.sessionStorage.setItem('RESET_PASSWORD_PRECLUDED', true);
      errorCode = 'ER123';
    }
    return this.helper.showLabel(errorCode);
  }
  /**
   * Show error on UI and also return the same error.
   * @param  {*} error
   * @return Promise<string> Returned error code.
   */
  handleAccountCreationError(error) {
    const errorMessage = get(error, 'error.message', error);
    const statusCode = Number(get(error, 'statusCode', error.status));
    let errorCode;
    switch (statusCode) {
      case 400:
        errorCode = 'ER20';
        if (errorMessage && errorMessage.indexOf('find') > -1) {
          errorCode = 'ER71';
        }
        break;
      case 403:
        errorCode = 'ER8';
        break;
      case 432:
        errorCode = 'ER9';
        break;
      case 433:
      case 436:
        errorCode = 'ER86';
        break;
      case 437:
        errorCode = 'ER9';
        break;
      case 434:
        errorCode = 'ER60';
        break;
      case 435:
        errorCode = 'ER51';
        break;
      default:
        errorCode = 'ER71';
    }
    return this.helper.promise.reject(errorCode);
  }
  /**
   * Show error on UI and also return the same error.
   * @param  {*} error
   * @return Promise<string> Returned error code.
   */
  handlePackageCreateAccountError(error) {
    const errorMessage = get(error, 'error.message', '');
    const statusCode = Number(get(error, 'statusCode', error.status));
    let errorCode = 'ER71';
    if (statusCode === 400 || statusCode === 404) {
      errorCode = 'ER132';
      if (errorMessage.includes('already associated')) {
        errorCode = 'ER20';
      }
    }
    return this.helper.promise.reject(errorCode);
  }
  /**
   * Show error on UI and also return the same error.
   * @param  {*} error
   * @return Promise<string> Returned error code.
   */
  handleRegisterAccountError(error) {
    const errorMessage = get(error, 'error.message', '');
    const statusCode = Number(get(error, 'statusCode', error.status));
    let errorCode = 'ER71';
    if (statusCode === 400 || statusCode === 404) {
      errorCode = 'ER139';
      if (errorMessage.includes('already associated')) {
        errorCode = 'ER20';
      }
    }
    return this.helper.promise.reject(errorCode);
  }
  /**
   * bypass second time validation on same email address
   * @param {string} email
   * @return (boolean) return true or false.
   */
  skipValidation(email) {
    let bypassOnNextTry = false;
    if (this.helper.$emailInValidation.getValue() === email) {
      bypassOnNextTry = true;
      this.helper.$emailInValidation.next('');
    } else {
      this.helper.$emailInValidation.next(email);
    }
    return bypassOnNextTry;
  }
  async getAuthnHeaders(skip) {
    try {
      if (!skip) {
        await this.authN();
      }
      return {
        WCToken: this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TOKEN),
        WCTrustedToken: this.helper.localStorage.getItem(CacheKeys.HEADERS_WC_TRUSTED_TOKEN)
      };
    } catch (error) {
      throw error;
    }
  }
  async getInstanceId() {
    await this.createGuestInstance();
    return this.helper.getInstanceId();
  }
  async reset() {
    this.helper.clearGuest(true);
    try {
      const authNHeaders = await this.getAuthnHeaders(true);
      if (!isEmpty(authNHeaders)) {
        this.helper.clearAuthn();
        await this.authN();
      } else {
        await this.createGuestInstance();
      }
      this.helper.updateLoggedInStatus(false);
      return true;
    } catch (error) {
      throw error;
    }
  }
  ngOnDestroy() {
    this.subscriptions$?.unsubscribe();
  }
  static {
    this.ɵfac = function AccountMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AccountMsGuestsClass)(i0.ɵɵinject('AppDefaults'), i0.ɵɵinject(AccountMsGuestsService), i0.ɵɵinject(ProfileMsGuestsClass), i0.ɵɵinject(MsGuestBaseHelper), i0.ɵɵinject(i0.NgZone), i0.ɵɵinject(i1$1.DalService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AccountMsGuestsClass,
      factory: AccountMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AccountMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }, {
    type: AccountMsGuestsService
  }, {
    type: ProfileMsGuestsClass
  }, {
    type: MsGuestBaseHelper
  }, {
    type: i0.NgZone
  }, {
    type: i1$1.DalService
  }], null);
})();

// Angular
class AccountMsGuestsModule {
  static {
    this.ɵfac = function AccountMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AccountMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: AccountMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [AccountMsGuestsClass, AccountMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AccountMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [AccountMsGuestsClass, AccountMsGuestsService]
    }]
  }], null, null);
})();

/**
 * Export EntitlementsMsGuestsService
 */
class EntitlementsMsGuestsService extends MsGuestsBaseService {
  /**
   * Get Assignees
   * @param {string} guest_id
   * @returns {Promise<any>}
   */
  getAssignees(guest_id) {
    return this.doRequest(this.urls.ENTITLEMENTS.ASSIGNEES + guest_id, this.TYPE_GET).then(response => get(response, 'result.partyMembers', []));
  }
  /**
   * Create Assignee
   * @param assignee
   * @returns {Promise<any>}
   */
  createAssignee(assignee) {
    return this.doRequest(this.urls.ENTITLEMENTS.CREATE, this.TYPE_POST, assignee).then(response => get(response, 'result.sequenceId'));
  }
  /**
   * Get guest travel party members
   * @returns {Promise<any>}
   */
  getTravelPartyMembers(guest_id) {
    return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRAVEL_PARTY_MEMBERS, {
      guest_id
    }), this.TYPE_GET);
  }
  /**
   * Set guest travel party members
   * @returns {Promise<void>}
   */
  setTravelPartyMembers(guest_id, request, headers) {
    return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRAVEL_PARTY_MEMBERS, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
   * Update guest travel party members
   * @returns {Promise<void>}
   */
  updateTravelPartyMembers(guest_id, travel_party_member_id, request, headers) {
    return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRAVEL_PARTY_MEMBERS, {
      guest_id,
      travel_party_member_id
    }), this.TYPE_PATCH, request, headers);
  }
  /**
   * Load TCM content
   * @param {string} url
   * @returns {Promise<any>}
   */
  getEntitlementDetails(url) {
    return this.doRequest(url, this.TYPE_GET, null);
  }
  /**
   * Link entitlements to guestId
   * @param {ILinkEntitlements} request
   * @returns {Promise<any>}
   */
  linkToGuestId(request) {
    return this.doRequest(this.urls.ENTITLEMENTS.LINK, this.TYPE_POST, request);
  }
  /**
   * Get link method
   * @param {IGetLinkEntitlements} request
   * @returns {Promise<any>}
   */
  getLinkMethod(request) {
    return this.doRequest(this.urls.ENTITLEMENTS.ENTITLEMENTS_LINK_METHODS, this.TYPE_POST, request);
  }
  /**
   * Link search link
   * @param {ILinkSearchLink} request
   * @returns {Promise<any>}
   */
  linkSearchLink(request) {
    return this.doRequest(this.urls.ENTITLEMENTS.ENTITLEMENTS_LINK_SEARCH_LINK, this.TYPE_POST, request);
  }
  /**
   * Load TCM content
   * @param {Array<string>} IDList
   * @returns {Promise<any>}
   */
  loadTCMContent(IDList) {
    return this.doRequest(this.urls.TRIDION.ID_EQV_NAME + IDList, this.TYPE_GET);
  }
  /**
   * Link Guest Ticket
   * @param guest_id
   * @param {IEntitlementsLinkTicket} request
   * @returns {Promise<IGuestTravelPartyTicketResponse>}
   */
  linkTicket(guest_id, request, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.LINK_TICKET, {
      guest_id
    }), this.TYPE_POST, request, headers, undefined, undefined, true);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵEntitlementsMsGuestsService_BaseFactory;
      return function EntitlementsMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵEntitlementsMsGuestsService_BaseFactory || (ɵEntitlementsMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(EntitlementsMsGuestsService)))(__ngFactoryType__ || EntitlementsMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: EntitlementsMsGuestsService,
      factory: EntitlementsMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EntitlementsMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class EntitlementsMsGuestsClass {
  /**
   * Constructor
   * @param {EntitlementsMsGuestsService} service
   * @param {MsGuestBaseHelper} helper
   */
  constructor(service, helper) {
    this.service = service;
    this.helper = helper;
  }
  /**
   * Create assignee
   * @param assignee
   * @param {IEntitlementPartyMember[]} assignee
   * @param {IEntitlementPartyMember[]} previousAssignees
   * @param {number} retry
   * @param {string} guest_id
   * @returns {Promise<any>}
   */
  createAssignee(assignee, source_id, previousAssignees = [], retry = 3, guest_id = this.helper.getGuestId()) {
    if (!guest_id && isEmpty(assignee) || isEmpty(assignee) || !source_id) {
      return this.helper.promise.reject('Missing guest_id, source_id or assignee info: ', {
        guest_id,
        source_id,
        assignee
      });
    }
    let newSequenceId = 0;
    this.helper.spinner.show(SpinnerKeys.CREATE_ASSIGNEE);
    const getResponse = !this.helper.isAuth() ? Promise.resolve().then(() => {
      if (!isEmpty(previousAssignees)) {
        previousAssignees = sortBy(previousAssignees, 'sequenceId');
        newSequenceId = get(previousAssignees[previousAssignees.length - 1], 'sequenceId', 0) + 1;
      }
      assignee.sequenceId = newSequenceId;
      previousAssignees.push(assignee);
      return this.getAssignees(undefined, previousAssignees);
    }) : Promise.resolve().then(() => {
      const newAssignee = {
        sourceId: source_id,
        guestId: guest_id,
        partyMemberFirstName: assignee.firstName,
        partyMemberLastName: assignee.lastName,
        partyMemberSuffix: assignee.suffix
      };
      return this.service.createAssignee(newAssignee).then(sequenceId => {
        newSequenceId = sequenceId;
        return this.getAssignees();
      });
    });
    return getResponse.then(assignees => {
      this.helper.spinner.hide(SpinnerKeys.CREATE_ASSIGNEE);
      return {
        newSequenceId: newSequenceId,
        assignees: assignees
      };
    }).catch(error => {
      if (retry > 0) {
        return this.createAssignee(assignee, source_id, previousAssignees, retry - 1);
      }
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * Get Assignees
   * @param {string} guest_id
   * @param {IEntitlementPartyMember} guestResponse
   * @returns {Promise<any>}
   */
  getAssignees(guest_id = this.helper.getGuestId(), guestResponse) {
    if (!guest_id && isEmpty(guestResponse)) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_ASSIGNEES);
    const getResponse = !isEmpty(guestResponse) ? Promise.resolve(guestResponse) : this.service.getAssignees(guest_id);
    return getResponse.then(assignees => {
      this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_ASSIGNEES);
      assignees = sortBy(assignees, 'firstName');
      assignees = map(assignees, (assignee, index) => {
        assignee.isOwner = assignee.sequenceId === 0;
        assignee.id = index;
        assignee.label = [assignee.firstName, assignee.lastName, assignee.suffix].filter(Boolean).join(' ');
        return assignee;
      });
      assignees.push({
        suffix: '',
        firstName: '',
        sequenceId: 'add_new_name',
        label: get(this.helper.pageLabels, 'AddNewNameLabel'),
        lastName: get(this.helper.pageLabels, 'AddNewNameLabel')
      });
      return assignees;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(error);
    });
  }
  /**
   * **NEW** Get Travel Party Members
   * @returns {Promise<ITravelPartyMembersResponse>}
   */
  getTravelPartyMembers(guest_id = this.helper.getGuestId()) {
    this.helper.spinner.show(SpinnerKeys.GET_TRAVEL_PARTY_MEMBERS);
    return this.service.getTravelPartyMembers(guest_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_TRAVEL_PARTY_MEMBERS);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * **NEW** Set Travel Party Member
   * @returns {Promise<void>}
   */
  setTravelPartyMembers(request, guest_id = this.helper.getGuestId()) {
    this.helper.spinner.show(SpinnerKeys.SET_TRAVEL_PARTY_MEMBERS);
    const sourceId = this.helper.getSourceId('WebAssignEntitlements');
    const email = this.helper.getLoginUserName();
    const headers = {
      'x-source-id': sourceId,
      'iv-user': email
    };
    return this.service.setTravelPartyMembers(guest_id, request, headers).then(response => {
      this.helper.spinner.hide(SpinnerKeys.SET_TRAVEL_PARTY_MEMBERS);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * **NEW** Set Travel Party Member
   * @returns {Promise<void>}
   */
  updateTravelPartyMembers(request, travel_party_member_id, guest_id = this.helper.getGuestId()) {
    this.helper.spinner.show(SpinnerKeys.UPDATE_TRAVEL_PARTY_MEMBERS);
    const sourceId = this.helper.getSourceId('WebAssignEntitlements');
    const email = this.helper.getLoginUserName();
    const etag = this.helper.getEtag();
    const headers = {
      'x-source-id': sourceId,
      'iv-user': email,
      'If-Match': etag
    };
    return this.service.updateTravelPartyMembers(guest_id, travel_party_member_id, request, headers).then(response => {
      this.helper.spinner.hide(SpinnerKeys.UPDATE_TRAVEL_PARTY_MEMBERS);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get Entitlement Details
   * @param {Array<string>} ids
   * @returns {Promise<any>}
   */
  getEntitlementDetails(ids) {
    this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
    return this.service.getEntitlementDetails(this.helper.getRelativeUrl('/' + this.helper.relativeUrls.FETCH_TRIDION_ID_EQV_NAME + ids)).then(() => {
      this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
    }).catch(() => {
      this.helper.spinner.show(SpinnerKeys.OVERRIDE);
    });
  }
  /**
   * Get entitlement details
   * @param {Array<string>} ids
   * @returns {Promise<any>}
   */
  getDetails(ids) {
    if (!ids) {
      return this.helper.promise.reject('Missing ids: ', {
        ids
      });
    }
    this.helper.spinner.show(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
    return this.service.loadTCMContent(ids).then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_ENTITLEMENT_DETAILS);
      return get(response, 'result');
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get link method
   * @param {string} media_id
   * @param {string} guest_id
   * @returns {Promise<any | void> | Promise<HttpResponse<any> | void>}
   */
  getLinkMethod(media_id, guest_id = this.helper.getGuestId()) {
    if (!media_id) {
      return this.helper.promise.reject('Missing media id: ', {
        media_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.GET_LINK_ENTITLEMENT);
    return this.service.getLinkMethod({
      mediaId: media_id,
      guestId: guest_id
    }).then(res => {
      this.helper.spinner.hide(SpinnerKeys.GET_LINK_ENTITLEMENT);
      const result = get(res, 'result');
      const mediaStatus = get(res, 'result.mediaStatus', '');
      if (!result || !mediaStatus) {
        return;
      }
      if (mediaStatus !== 'Valid') {
        throw mediaStatus;
      }
      if (!result.name && !result.salesProgramId) {
        res = 'noSecondFactor';
      } else if (result.name) {
        res = 'name';
      } else if (result.salesProgramId) {
        res = 'salesProgramId';
      }
      return res;
    }).catch(mediaStatus => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(this.getEntitlementErrorCode(mediaStatus) || 'ER89');
    });
  }
  /**
   * Link search link
   * @param {string} media_id
   * @returns {Promise<any>}
   */
  linkSearchLink(media_id) {
    if (!media_id) {
      return this.helper.promise.reject('Missing media id: ', {
        media_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.ENTITLEMENTS_LINK_SEARCH_LINK);
    return this.service.linkSearchLink({
      mediaId: media_id
    }).then(res => {
      this.helper.spinner.hide(SpinnerKeys.ENTITLEMENTS_LINK_SEARCH_LINK);
      return get(res, 'result', []);
    }).catch(err => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(err);
    });
  }
  /**
   * Link entitlements to guest_id
   * @param {string | []} ids
   * @param {string} source_id
   * @param {string | number} sequence_id
   * @param {string} invalidFactor
   * @param {string} guest_id
   * @returns {Promise<HttpResponse<any>> | Promise<any | void>}
   */
  linkToGuestId(ids, source_id, sequence_id, invalidFactor = '', guest_id = this.helper.getGuestId()) {
    if (!ids) {
      return this.helper.promise.reject('Missing ids: ', {
        ids
      });
    }
    this.helper.spinner.show(SpinnerKeys.LINK_ENTITLEMENT);
    return this.service.linkToGuestId({
      mediaIds: ids,
      toGuestId: guest_id,
      sourceId: source_id,
      sequenceId: sequence_id
    }).then(res => {
      this.helper.spinner.hide(SpinnerKeys.LINK_ENTITLEMENT);
      const mediaStatus = get(res, 'result.mediaStatus');
      if (mediaStatus !== 'Valid') {
        return this.helper.promise.reject(res);
      }
      return get(res, 'result.entitlements');
    }).catch(err => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      const mediaStatus = get(err, 'result.mediaStatus');
      const salesIdError = invalidFactor === 'InvalidSecondFactorSalesId' && mediaStatus === 'InvalidSecondFactor';
      return this.helper.promise.reject(salesIdError ? invalidFactor : err);
    });
  }
  /**
   * **NEW** Link ticket
   * @param {IEntitlementsLinkTicket} request
   * @returns {Promise<IGuestLinkTicket>}
   */
  linkTicket(request, headers) {
    this.helper.spinner.show(SpinnerKeys.LINK_ENTITLEMENT);
    const guest_id = this.helper.getGuestId();
    return this.service.linkTicket(guest_id, request, headers).then(response => {
      this.helper.spinner.hide(SpinnerKeys.LINK_ENTITLEMENT);
      return response;
    }).catch(err => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(err);
    });
  }
  /**
   * Get entitlement error code
   * @param {string} status
   * @returns {string}
   */
  getEntitlementErrorCode(status) {
    let error;
    switch (status) {
      case 'InvalidMedia':
      case 'NoEntitlementExists':
        error = 'ER89';
        break;
      case 'InvalidLinkEmployee':
        error = 'ER117';
        break;
      case 'TooManyEntitlements':
        error = 'ER77';
        break;
      case 'AccountLocked24Hour':
        error = 'ER100';
        break;
      case 'AccountLockedNew':
      case 'AccountLocked':
        error = 'ER92';
        break;
      case 'InvalidLinkRegistered':
        error = 'ER119';
        break;
      case 'InvalidGuest':
        error = 'ER71';
        break;
      case 'InvalidLinkExpired':
      case 'InvalidLinkRedeemed':
      case 'InvalidLinkCancelled':
        error = 'ER118';
        break;
      case 'InvalidSecondFactor':
        error = 'ER90';
        break;
      case 'InvalidSecondFactorSalesId':
        error = 'ER91';
        break;
      default:
        error = '';
    }
    return error;
  }
  static {
    this.ɵfac = function EntitlementsMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || EntitlementsMsGuestsClass)(i0.ɵɵinject(EntitlementsMsGuestsService), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: EntitlementsMsGuestsClass,
      factory: EntitlementsMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EntitlementsMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: EntitlementsMsGuestsService
  }, {
    type: MsGuestBaseHelper
  }], null);
})();

// Angular
class EntitlementsMsGuestsModule {
  static {
    this.ɵfac = function EntitlementsMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || EntitlementsMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: EntitlementsMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [EntitlementsMsGuestsClass, EntitlementsMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(EntitlementsMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [EntitlementsMsGuestsClass, EntitlementsMsGuestsService]
    }]
  }], null, null);
})();

/**
 * Export PersonalMsGuestsService
 */
class PersonalMsGuestsService extends MsGuestsBaseService {
  /**
   * Get Personal information
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IPersonalInfo>}
   */
  getInfo(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Get Personal addresses
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IAddress[]>}
   */
  getAddresses(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Create Personal address
   * @param {IAddress} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  createAddress(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
   * Get Personal address
   * @param {string} guest_id
   * @param {string} address_id
   * @param {{}} headers
   * @returns {Promise<IAddress>}
   */
  getAddress(guest_id, address_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
      guest_id,
      address_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Update Personal address
   * @param {IAddress} request
   * @param {string} guest_id
   * @param {string} address_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateAddress(request, guest_id, address_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
      guest_id,
      address_id
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Set Email Commerce Address Request
   * @param {string} url
   * @param {any} request
   * @param {HttpHeaders | Record<string, string | string[]>} headers
   * @returns {Promise<any>}
   */
  setEmailCommerceAddressRequest(url, request, headers) {
    return this.doRequest(url, this.TYPE_POST, request, headers);
  }
  /**
   * Delete address
   * @param {string} guest_id
   * @param {string} address_id
   * @param {{}} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deleteAddress(guest_id, address_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_ADDRESSES_SINGLE, {
      guest_id,
      address_id
    }), this.TYPE_DELETE, undefined, headers);
  }
  /**
   * Get Personal contact information
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IContact[]>}
   */
  getContactInfo(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_CONTACTS, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Update Personal contact Info
   * @param {IContact} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateContactInfo(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_CONTACTS, {
      guest_id
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Get Personal Emails
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IEmail[]>}
   */
  getEmails(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Create Personal Email
   * @param {IEmailCreateRequest} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  createEmail(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
   * Get Personal Email
   * @param {string} guest_id
   * @param {string} email_address
   * @param {{}} headers
   * @returns {Promise<IEmail>}
   */
  getEmail(guest_id, email_address, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
      guest_id,
      email_address
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Update Personal Email
   * @param {IEmailCreateRequest} request
   * @param {string} guest_id
   * @param {string} email_address
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateEmail(request, guest_id, email_address, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
      guest_id,
      email_address
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Delete Personal Email
   * @param {string} guest_id
   * @param {string} email_address
   * @param {{}} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deleteEmail(guest_id, email_address, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_EMAILS_SINGLE, {
      guest_id,
      email_address
    }), this.TYPE_DELETE, undefined, headers);
  }
  /**
   * Get Personal phones
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IPhone[]>}
   */
  getPhones(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Create Personal Phone
   * @param {IPhoneCreateRequest} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  createPhone(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
   * Get Personal phone
   * @param {string} guest_id
   * @param {string} phone_number
   * @param {{}} headers
   * @returns {Promise<IPhone>}
   */
  getPhone(guest_id, phone_number, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
      guest_id,
      phone_number
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Update Personal Phone
   * @param {IPhoneCreateRequest} request
   * @param {string} guest_id
   * @param {string} phone_number
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  updatePhone(request, guest_id, phone_number, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
      guest_id,
      phone_number
    }), this.TYPE_PUT, request, headers);
  }
  /**
   * Delete Personal Phone
   * @param {string} guest_id
   * @param {string} phone_number
   * @param {{}} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deletePhone(guest_id, phone_number, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PERSONAL_INFO_PHONES_SINGLE, {
      guest_id,
      phone_number
    }), this.TYPE_DELETE, undefined, headers);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵPersonalMsGuestsService_BaseFactory;
      return function PersonalMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵPersonalMsGuestsService_BaseFactory || (ɵPersonalMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(PersonalMsGuestsService)))(__ngFactoryType__ || PersonalMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PersonalMsGuestsService,
      factory: PersonalMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PersonalMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

// Angular
/**
 * Export PersonalMsGuestsClass
 */
class PersonalMsGuestsClass {
  /**
   * Constructor
   * @param {PersonalMsGuestsService} service
   * @param {MsGuestBaseHelper} helper
   */
  constructor(service, account, helper) {
    this.service = service;
    this.account = account;
    this.helper = helper;
  }
  /**
   * Get Personal Info
   * @param {string} guest_id
   * @param {object} headers
   * @returns {Promise<IPersonalInfo>}
   */
  getInfo(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    return this.service.getInfo(guest_id, headers);
  }
  /**
   * Get Personal addresses
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IAddress[]>}
   */
  getAddresses(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    return this.service.getAddresses(guest_id, headers);
  }
  /**
   * Create Personal Address
   * @param {IAddress} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  createAddress(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    headers['x-source-id'] = sourceId;
    return this.service.createAddress(request, guest_id, headers);
  }
  /**
   * Get Personal Address
   * @param {string} address_id
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IAddress>}
   */
  getAddress(address_id, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !address_id) {
      return this.helper.promise.reject('Missing guest_id or address_id: ', {
        guest_id,
        address_id
      });
    }
    return this.service.getAddress(guest_id, address_id, headers);
  }
  /**
   * Get Primary Address
   * @returns {Promise<IAddress>}
   */
  getPrimaryAddress() {
    this.helper.spinner.show(SpinnerKeys.GET_PRIMARY_ADDRESS);
    return this.getAddresses().then(request => {
      this.helper.spinner.hide(SpinnerKeys.GET_PRIMARY_ADDRESS);
      if (!request || !request.addresses) {
        return;
      }
      const primaryAddress = find(request.addresses, address => {
        return address.addressId;
      });
      if (primaryAddress) {
        primaryAddress.primary = DefaultValue.PRIMARY_SHIPPING_ADDRESS_INDICATOR;
      }
      return primaryAddress;
    });
  }
  /**
   * Update personal address
   * @param {IAddress} request
   * @param {string} sourceId
   * @param {string} address_id
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateAddress(request, sourceId, address_id, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !address_id || !etag) {
      return this.helper.promise.reject('Missing guest_id, address_id or etag: ', {
        guest_id,
        address_id,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    this.helper.spinner.show(SpinnerKeys.UPDATE_PERSONAL_ADDRESS);
    return this.service.updateAddress(request, guest_id, address_id, headers).then(response => {
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU9', 'success');
      this.helper.spinner.hide(SpinnerKeys.UPDATE_PERSONAL_ADDRESS);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Set Email Commerce Address Request
   * @param {IAddress | any} addressInfo
   * @returns {Promise<any>}
   */
  async setEmailCommerceAddressRequest(addressInfo) {
    this.helper.spinner.show(SpinnerKeys.SET_COMMERCE_ADDRESS_REQUEST);
    const contact = this.helper.addressToContact(addressInfo);
    contact.sourceId = this.helper.getSourceId('WebEditContactInfo');
    const request = {
      contact: [contact]
    };
    const headers = await this.account.getAuthnHeaders();
    return this.service.setEmailCommerceAddressRequest(this.helper.getRelativeUrl(this.helper.relativeUrls.SHIPPING_ADDRESS_SAVE), request, headers).then(response => {
      this.helper.spinner.hide(SpinnerKeys.SET_COMMERCE_ADDRESS_REQUEST);
      return {
        address: contact,
        addressId: response.addressId
      };
    }).catch(error => {
      const isParamMissing = get(error, 'error.errors.0.errorKey') === '_ERR_MISSING_CMD_PARAMETER';
      const isFlBillingError = get(error, 'error.errors.0.errorKey') === '_ERR_NOT_BILLING_ZIPCODE';
      let errorMessage = isParamMissing ? get(error, 'error.errors.0.errorMessage', '') : '';
      if (isFlBillingError) {
        errorMessage = this.helper.pageLabels.ER58;
      }
      if (!errorMessage) {
        errorMessage = get(error, 'data.message');
        const errors = get(error, 'data.rawError.errors', get(error, 'data.rawError', get(error, 'data.errors')));
        if (errorMessage) {
          errorMessage = get(errors, '[0].errorMessage');
        }
      }
      this.helper.showLabel(errorMessage || this.helper.pageLabels.ER71);
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.promise.reject(errorMessage);
    });
  }
  /**
   * Delete Personal address
   * @param {string} sourceId
   * @param {string} address_id
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deleteAddress(sourceId, address_id, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !address_id || !etag) {
      return this.helper.promise.reject('Missing guest_id, address_id or etag: ', {
        guest_id,
        address_id,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    this.helper.spinner.show(SpinnerKeys.DELETE_PERSONAL_ADDRESS);
    return this.service.deleteAddress(guest_id, address_id, headers).then(response => {
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU10', 'success');
      this.helper.spinner.hide(SpinnerKeys.DELETE_PERSONAL_ADDRESS);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get Personal Contact Info
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IContact[]>}
   */
  getContactInfo(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id');
    }
    return this.service.getContactInfo(guest_id, headers);
  }
  /**
   * Update Contact Info
   * @param {IContact} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateContactInfo(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !etag) {
      return this.helper.promise.reject('Missing guest_id or etag: ', {
        guest_id,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    this.helper.spinner.show(SpinnerKeys.UPDATE_PERSONAL_CONTACT_INFO);
    return this.service.updateContactInfo(request, guest_id, headers).then(response => {
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU7', 'success');
      this.helper.spinner.hide(SpinnerKeys.UPDATE_PERSONAL_CONTACT_INFO);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER84');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get Personal emails
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IEmail[]>>
   */
  getEmails(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    return this.service.getEmails(guest_id, headers);
  }
  /**
   * Create Personal Email
   * @param {IEmailCreateRequest} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  createEmail(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    headers['x-source-id'] = sourceId;
    return this.service.createEmail(request, guest_id, headers);
  }
  /**
   * Get personal email
   * @param {string} email_address
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IEmail>}
   */
  getEmail(email_address, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !email_address) {
      return this.helper.promise.reject('Missing guest_id or email_address: ', {
        guest_id,
        email_address
      });
    }
    return this.service.getEmail(guest_id, email_address, headers);
  }
  /**
   * Update Personal Email
   * @param {IEmailCreateRequest} request
   * @param {string} sourceId
   * @param {string} email_address
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updateEmail(request, sourceId, email_address, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !email_address || !etag) {
      return this.helper.promise.reject('Missing guest_id, email_address or etag: ', {
        guest_id,
        email_address,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    return this.service.updateEmail(request, guest_id, email_address, headers);
  }
  /**
   * Delete Personal Email
   * @param {string} sourceId
   * @param {string} email_address
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deleteEmail(sourceId, email_address, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !email_address || !etag) {
      return this.helper.promise.reject('Missing guest_id, email_address or etag: ', {
        guest_id,
        email_address,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    return this.service.deleteEmail(guest_id, email_address, headers);
  }
  /**
   * Get Personal Phones
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IPhone[]>}
   */
  getPhones(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    return this.service.getPhones(guest_id, headers);
  }
  /**
   * Create Personal phone
   * @param {IPhoneCreateRequest} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  createPhone(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    headers['x-source-id'] = sourceId;
    return this.service.createPhone(request, guest_id, headers);
  }
  /**
   * Get Personal phone
   * @param {string} phone_number
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IPhone>}
   */
  getPhone(phone_number, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !phone_number) {
      return this.helper.promise.reject('Missing guest_id or phone_number: ', {
        guest_id,
        phone_number
      });
    }
    return this.service.getPhone(guest_id, phone_number, headers);
  }
  /**
   * Update Personal phone
   * @param {IPhoneCreateRequest} request
   * @param {string} sourceId
   * @param {string} phone_number
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IGenericResponse>}
   */
  updatePhone(request, sourceId, phone_number, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !phone_number || !etag) {
      return this.helper.promise.reject('Missing guest_id, phone_number or etag: ', {
        guest_id,
        phone_number,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    return this.service.updatePhone(request, guest_id, phone_number, headers);
  }
  /**
   * Delete Personal phone
   * @param {string} sourceId
   * @param {string} phone_number
   * @param {string} guest_id
   * @param {string} etag
   * @param {any} headers
   * @returns {Promise<IDocumentCreated>}
   */
  deletePhone(sourceId, phone_number, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !phone_number || !etag) {
      return this.helper.promise.reject('Missing guest_id, phone_number or etag: ', {
        guest_id,
        phone_number,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    return this.service.deletePhone(guest_id, phone_number, headers);
  }
  static {
    this.ɵfac = function PersonalMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PersonalMsGuestsClass)(i0.ɵɵinject(PersonalMsGuestsService), i0.ɵɵinject(AccountMsGuestsClass), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PersonalMsGuestsClass,
      factory: PersonalMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PersonalMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: PersonalMsGuestsService
  }, {
    type: AccountMsGuestsClass
  }, {
    type: MsGuestBaseHelper
  }], null);
})();

// Angular
class PersonalMsGuestsModule {
  static {
    this.ɵfac = function PersonalMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PersonalMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: PersonalMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [PersonalMsGuestsClass, PersonalMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PersonalMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [PersonalMsGuestsClass, PersonalMsGuestsService]
    }]
  }], null, null);
})();

// Service
/**
 * Export PreferencesMsGuestsService
 */
class PreferencesMsGuestsService extends MsGuestsBaseService {
  /**
   * Get Preferences
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IPreferences>}
   */
  getPreferences(guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, {
      guest_id
    }), this.TYPE_GET, undefined, headers);
  }
  /**
   * Create user Preferences
   * @param {IPreferences} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  createPreferences(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, {
      guest_id
    }), this.TYPE_POST, request, headers);
  }
  /**
   * Update user preferences
   * @param {IPreferences} request
   * @param {string} guest_id
   * @param {{}} headers
   * @returns {Promise<IGenericResponse>}
   */
  updatePreferences(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.GUESTS.PREFERENCES, {
      guest_id
    }), this.TYPE_PUT, request, headers);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵPreferencesMsGuestsService_BaseFactory;
      return function PreferencesMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵPreferencesMsGuestsService_BaseFactory || (ɵPreferencesMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(PreferencesMsGuestsService)))(__ngFactoryType__ || PreferencesMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PreferencesMsGuestsService,
      factory: PreferencesMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PreferencesMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();

// Angular
/**
 * Export PreferencesMsGuestsClass
 */
class PreferencesMsGuestsClass {
  /**
   * Constructor
   * @param {PreferencesMsGuestsService} service
   * @param {MsGuestBaseHelper} helper
   */
  constructor(service, helper) {
    this.service = service;
    this.helper = helper;
  }
  /**
   * Get Personal preferences
   * @param {string} guest_id
   * @param {any} headers
   * @returns {Promise<IPreferences>}
   */
  getPreferences(guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    return this.service.getPreferences(guest_id, headers);
  }
  /**
   * Create preferences
   * @param {IPreferences} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param headers
   * @returns {Promise<IGenericResponse>}
   */
  createPreferences(request, sourceId, guest_id = this.helper.getGuestId(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    headers['x-source-id'] = sourceId;
    return this.service.createPreferences(request, guest_id, headers);
  }
  /**
   * Update preferences
   * @param {IPreferences} request
   * @param {string} sourceId
   * @param {string} guest_id
   * @param {string} etag
   * @param headers
   * @returns {Promise<IGenericResponse>}
   */
  updatePreferences(request, sourceId, guest_id = this.helper.getGuestId(), etag = this.helper.getEtag(), headers = this.helper.getNoCacheHeaders()) {
    if (!guest_id || !etag) {
      return this.helper.promise.reject('Missing guest_id or etag: ', {
        guest_id,
        etag
      });
    }
    headers['x-source-id'] = sourceId;
    headers['If-Match'] = etag;
    this.helper.spinner.show(SpinnerKeys.UPDATE_PREFERENCES);
    return this.service.updatePreferences(request, guest_id, headers).then(response => {
      this.helper.sessionStorage.setItem(CacheKeys.RETAIN_NOTIFICATION, 'true');
      this.helper.showLabel('SU6', 'success');
      this.helper.spinner.hide(SpinnerKeys.UPDATE_PREFERENCES);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  static {
    this.ɵfac = function PreferencesMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PreferencesMsGuestsClass)(i0.ɵɵinject(PreferencesMsGuestsService), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PreferencesMsGuestsClass,
      factory: PreferencesMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PreferencesMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: PreferencesMsGuestsService
  }, {
    type: MsGuestBaseHelper
  }], null);
})();

// Angular
class PreferencesMsGuestsModule {
  static {
    this.ɵfac = function PreferencesMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PreferencesMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: PreferencesMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [PreferencesMsGuestsClass, PreferencesMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PreferencesMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [PreferencesMsGuestsClass, PreferencesMsGuestsService]
    }]
  }], null, null);
})();

// Angular
class ProfileMsGuestsModule {
  static {
    this.ɵfac = function ProfileMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ProfileMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: ProfileMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [ProfileMsGuestsClass, ProfileMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ProfileMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [ProfileMsGuestsClass, ProfileMsGuestsService]
    }]
  }], null, null);
})();

// Service
/**
 * Export WalletMsGuestsService
 */
class WalletMsGuestsService extends MsGuestsBaseService {
  getFinancialTransactions(guest_id) {
    return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.TRANSACTIONS, {
      guest_id
    }), this.TYPE_GET);
  }
  /**
   * Get wallet
   * @returns {Promise<any>}
   */
  getWallet() {
    return this.doRequest(this.urls.FOLIO.WALLET, this.TYPE_GET);
  }
  /**
   * Get tickets
   * @returns {Promise<any>}
   */
  getTickets(guest_id, resort_area_code) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.TICKETS, {
      guest_id,
      resort_area_code
    }), this.TYPE_GET);
  }
  getPaymentCards(guest_id) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARDS, {
      guest_id
    }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
  }
  getPaymentCard(guest_id, payment_card_id) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARD, {
      guest_id,
      payment_card_id
    }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
  }
  hasPin(guest_id) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.HAS_PIN, {
      guest_id
    }), this.TYPE_GET, undefined, undefined, undefined, undefined, undefined, true);
  }
  setPin(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.SET_PIN, {
      guest_id
    }), this.TYPE_POST, request, headers, undefined, undefined, undefined, true);
  }
  setTravelPartySpendingLimit(request, member_id, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.TRAVEL_PARTY.SET_SPENDING_LIMIT, {
      guest_id: guest_id,
      travel_party_member_id: member_id
    }), this.TYPE_PUT, request, headers);
  }
  saveCard(request, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.PAYMENT_CARDS, {
      guest_id: guest_id
    }), this.TYPE_POST, request, headers, undefined, undefined, undefined, true);
  }
  updateCard(request, guest_id, payment_card_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.UPDATE_PAYMENT_CARD, {
      guest_id: guest_id,
      payment_card_id: payment_card_id
    }), this.TYPE_PUT, request, headers, undefined, undefined, undefined, true);
  }
  setPrimaryCard(guest_id, payment_card_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.MAKE_PRIMARY_CARD, {
      guest_id: guest_id,
      payment_card_id: payment_card_id
    }), this.TYPE_POST, undefined, headers, undefined, undefined, undefined, true);
  }
  deleteCard(payment_card_id, guest_id, headers) {
    return this.doRequest(this.replaceKey(this.urls.WALLET.UPDATE_PAYMENT_CARD, {
      guest_id: guest_id,
      payment_card_id: payment_card_id
    }), this.TYPE_DELETE, undefined, headers, undefined, undefined, undefined, true);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵWalletMsGuestsService_BaseFactory;
      return function WalletMsGuestsService_Factory(__ngFactoryType__) {
        return (ɵWalletMsGuestsService_BaseFactory || (ɵWalletMsGuestsService_BaseFactory = i0.ɵɵgetInheritedFactory(WalletMsGuestsService)))(__ngFactoryType__ || WalletMsGuestsService);
      };
    })();
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: WalletMsGuestsService,
      factory: WalletMsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WalletMsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class WalletMsGuestsClass {
  /**
   * Constructor
   * @param {WalletMsGuestsService} service
   * @param {MsGuestBaseHelper} helper
   * @param appDefaults
   */
  constructor(service, helper, appDefaults) {
    this.service = service;
    this.helper = helper;
    this.appDefaults = appDefaults;
  }
  getFinancialTransactions(guest_id = this.helper.getGuestId()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.GET_TRANSACTION_HISTORY);
    return this.service.getFinancialTransactions(guest_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_TRANSACTION_HISTORY);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER94');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get wallet
   * @returns {Promise<any>}
   */
  getWallet() {
    this.helper.spinner.show(SpinnerKeys.GET_WALLET);
    return this.service.getWallet().then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_WALLET);
      return get(response, 'result', {});
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Get Tickets
   * @returns {Promise<any>}
   */
  getTickets(guest_id, resort_area_code) {
    this.helper.spinner.show(SpinnerKeys.GET_TICKETS);
    return this.service.getTickets(guest_id, resort_area_code).then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_TICKETS);
      return response;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      const statusCode = get(error, 'status', '');
      if (statusCode === 404) {
        return [];
      }
      this.helper.showLabel('ER71');
      return this.helper.catchError(error);
    });
  }
  getPaymentCards(guest_id = this.helper.getGuestId()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.LOAD_CARDS);
    return this.service.getPaymentCards(guest_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.LOAD_CARDS);
      return response;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  getPaymentCard(payment_card_id, guest_id = this.helper.getGuestId()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    if (!payment_card_id) {
      return this.helper.promise.reject('Missing payment_card_id: ', {
        payment_card_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.GET_PAYMENT_CARD);
    return this.service.getPaymentCard(guest_id, payment_card_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.GET_PAYMENT_CARD);
      return response;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  /**
   * Set card PIN
   * @param {string} pin
   * @param {boolean} isSameCreated
   * @param {string} guest_id
   * @returns {Promise<any>}
   */
  setPin(pin, isSameCreated, guest_id = this.helper.getGuestId()) {
    if (!guest_id || !pin) {
      return this.helper.promise.reject('Missing guest_id or pin: ', {
        guest_id,
        pin
      });
    }
    this.helper.spinner.show(SpinnerKeys.SET_CARD_PIN);
    const request = {
      pin
    };
    const headers = {
      'x-source-id': this.helper.getSourceId('WebPIN')
    };
    return this.service.setPin(request, guest_id, headers).then(response => {
      console.log('success');
      this.helper.showLabel(isSameCreated ? 'SU14' : 'SU15', 'success');
      this.helper.spinner.hide(SpinnerKeys.SET_CARD_PIN);
      return response;
    }).catch(error => {
      console.log('error!');
      this.helper.showLabel(isSameCreated ? 'ER69' : 'ER70');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError('Error submitting pin: ' + error);
    });
  }
  hasPin(guest_id = this.helper.getGuestId()) {
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    this.helper.spinner.show(SpinnerKeys.HAS_PIN);
    return this.service.hasPin(guest_id).then(response => {
      this.helper.spinner.hide(SpinnerKeys.HAS_PIN);
      return response;
    }).catch(error => {
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  setTravelPartySpendingLimit(request, member_id, guest_id = this.helper.getGuestId()) {
    if (isEmpty(request)) {
      return this.helper.promise.reject('Missing request values: ', request);
    }
    this.helper.spinner.show(SpinnerKeys.SET_SPENDING_LIMIT);
    const headers = {
      'x-source-id': this.helper.getSourceId('WebSpendLimit')
    };
    return this.service.setTravelPartySpendingLimit(request, member_id, guest_id, headers).then(response => {
      if (response === '') {
        return this.helper.promise.reject('Failed to set spending limits: ', response);
      }
      this.helper.showLabel('SU18', 'success');
      this.helper.spinner.hide(SpinnerKeys.SET_SPENDING_LIMIT);
      return response;
    }).catch(error => {
      this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  saveCard(request, guest_id = this.helper.getGuestId()) {
    if (isEmpty(request)) {
      return this.helper.promise.reject('Missing request values: ', request);
    }
    if (!guest_id) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guest_id
      });
    }
    const {
      UPR_MOBILE_APIKEY,
      RESORT_AREA_CODE,
      PERSONALIZE_RESORT_CODE
    } = this.appDefaults;
    const headers = {
      'x-source-id': this.helper.getSourceId('WebCard'),
      'x-instance-id': this.helper.getInstanceId(),
      'x-uniwebservice-apikey': UPR_MOBILE_APIKEY,
      'x-resort-area-code': RESORT_AREA_CODE ?? PERSONALIZE_RESORT_CODE
    };
    this.helper.spinner.show(SpinnerKeys.SAVE_CARD_TO_ACC);
    return this.service.saveCard(request, guest_id, headers).then(() => {
      this.helper.spinner.hide(SpinnerKeys.SAVE_CARD_TO_ACC);
      this.helper.showLabel('SU6', 'success');
    }).catch(error => {
      get(error, 'status') === 400 ? this.helper.showLabel('ER35') : this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  updateCard(request, paymentCardId, guestId = this.helper.getGuestId(), etag = this.helper.getEtag()) {
    if (isEmpty(request)) {
      return this.helper.promise.reject('Missing request values: ', request);
    }
    if (!guestId) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guestId
      });
    }
    if (!paymentCardId) {
      return this.helper.promise.reject('Missing payment_card_id: ', {
        paymentCardId
      });
    }
    const {
      RESORT_AREA_CODE,
      PERSONALIZE_RESORT_CODE
    } = this.appDefaults;
    const headers = {
      'x-source-id': this.helper.getSourceId('WebCard'),
      'x-resort-area-code': RESORT_AREA_CODE ?? PERSONALIZE_RESORT_CODE,
      'If-Match': etag
    };
    this.helper.spinner.show(SpinnerKeys.SAVE_CARD_TO_ACC);
    return this.service.updateCard(request, guestId, paymentCardId, headers).then(() => {
      this.helper.spinner.hide(SpinnerKeys.SAVE_CARD_TO_ACC);
      this.helper.showLabel('SU6', 'success');
    }).catch(error => {
      get(error, 'status') === 400 ? this.helper.showLabel('ER35') : this.helper.showLabel('ER71');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  setPrimaryCard(paymentCardId, guestId = this.helper.getGuestId()) {
    if (!guestId) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guestId
      });
    }
    if (!paymentCardId) {
      return this.helper.promise.reject('Missing payment_card_id: ', {
        paymentCardId
      });
    }
    const headers = {
      'x-source-id': this.helper.getSourceId('WebCard')
    };
    this.helper.spinner.show(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD);
    return this.service.setPrimaryCard(guestId, paymentCardId, headers).then(() => this.helper.spinner.hide(SpinnerKeys.SET_PRIMARY_PAYMENT_CARD)).catch(error => {
      this.helper.showLabel('ER42');
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  deleteCard(cardId, guestId = this.helper.getGuestId(), etag = this.helper.getEtag()) {
    if (!guestId) {
      return this.helper.promise.reject('Missing guest_id: ', {
        guestId
      });
    }
    if (!cardId) {
      return this.helper.promise.reject('Missing payment_card_id: ', {
        cardId
      });
    }
    const headers = {
      'x-source-id': this.helper.getSourceId('WebCard'),
      'if-match': etag
    };
    this.helper.spinner.show(SpinnerKeys.DELETE_PAYMENT_CARD);
    return this.service.deleteCard(cardId, guestId, headers).then(response => {
      this.helper.showLabel('SU21', 'success');
      this.helper.spinner.hide(SpinnerKeys.DELETE_PAYMENT_CARD);
      return response;
    }).catch(error => {
      const errorMessage = get(error, 'error.invalid_params[0].rule') ?? get(error, 'error.detail') ?? '';
      const primaryCardErrors = ['Must select a new primary card before deleting the old', 'Cannot delete primary card'];
      if (error?.status === 400 && errorMessage && primaryCardErrors.includes(errorMessage)) {
        this.helper.showLabel('W11');
      } else {
        this.helper.showLabel('ER79');
      }
      this.helper.spinner.hide(SpinnerKeys.OVERRIDE);
      return this.helper.catchError(error);
    });
  }
  static {
    this.ɵfac = function WalletMsGuestsClass_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || WalletMsGuestsClass)(i0.ɵɵinject(WalletMsGuestsService), i0.ɵɵinject(MsGuestBaseHelper), i0.ɵɵinject('AppDefaults'));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: WalletMsGuestsClass,
      factory: WalletMsGuestsClass.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WalletMsGuestsClass, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: WalletMsGuestsService
  }, {
    type: MsGuestBaseHelper
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: ['AppDefaults']
    }]
  }], null);
})();

// Angular
class WalletMsGuestsModule {
  static {
    this.ɵfac = function WalletMsGuestsModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || WalletMsGuestsModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: WalletMsGuestsModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [WalletMsGuestsClass, WalletMsGuestsService]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(WalletMsGuestsModule, [{
    type: NgModule,
    args: [{
      providers: [WalletMsGuestsClass, WalletMsGuestsService]
    }]
  }], null, null);
})();

// Angular
class MsGuestModule {
  static {
    this.ɵfac = function MsGuestModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsGuestModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: MsGuestModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [PageLabelsModule, DalModule, NotificationModule, SpinnerModule, AccountMsGuestsModule, EntitlementsMsGuestsModule, PersonalMsGuestsModule, PreferencesMsGuestsModule, ProfileMsGuestsModule, WalletMsGuestsModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsGuestModule, [{
    type: NgModule,
    args: [{
      imports: [PageLabelsModule, DalModule, NotificationModule, SpinnerModule, AccountMsGuestsModule, EntitlementsMsGuestsModule, PersonalMsGuestsModule, PreferencesMsGuestsModule, ProfileMsGuestsModule, WalletMsGuestsModule]
    }]
  }], null, null);
})();

// Angular
/**
 * Export MsGuestsService
 */
class MsGuestsService {
  /**
   * Constructor
   * @param {AccountMsGuestsClass} account
   * @param {EntitlementsMsGuestsClass} entitlements
   * @param {ProfileMsGuestsClass} profile
   * @param {PersonalMsGuestsClass} personal
   * @param {PreferencesMsGuestsClass} preferences
   * @param {WalletMsGuestsClass} wallet
   * @param {MsGuestBaseHelper} helper
   */
  constructor(account, entitlements, profile, personal, preferences, wallet, helper) {
    this.account = account;
    this.entitlements = entitlements;
    this.profile = profile;
    this.personal = personal;
    this.preferences = preferences;
    this.wallet = wallet;
    this.helper = helper;
  }
  static {
    this.ɵfac = function MsGuestsService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || MsGuestsService)(i0.ɵɵinject(AccountMsGuestsClass), i0.ɵɵinject(EntitlementsMsGuestsClass), i0.ɵɵinject(ProfileMsGuestsClass), i0.ɵɵinject(PersonalMsGuestsClass), i0.ɵɵinject(PreferencesMsGuestsClass), i0.ɵɵinject(WalletMsGuestsClass), i0.ɵɵinject(MsGuestBaseHelper));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: MsGuestsService,
      factory: MsGuestsService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(MsGuestsService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: AccountMsGuestsClass
  }, {
    type: EntitlementsMsGuestsClass
  }, {
    type: ProfileMsGuestsClass
  }, {
    type: PersonalMsGuestsClass
  }, {
    type: PreferencesMsGuestsClass
  }, {
    type: WalletMsGuestsClass
  }, {
    type: MsGuestBaseHelper
  }], null);
})();
var TravelPartyGender;
(function (TravelPartyGender) {
  TravelPartyGender["male"] = "male";
  TravelPartyGender["female"] = "female";
  TravelPartyGender["other"] = "other";
})(TravelPartyGender || (TravelPartyGender = {}));
var TravelPartyRelationship;
(function (TravelPartyRelationship) {
  TravelPartyRelationship["SPOUSE"] = "SPOUSE";
  TravelPartyRelationship["CHILD"] = "CHILD";
  TravelPartyRelationship["OTHER"] = "OTHER";
})(TravelPartyRelationship || (TravelPartyRelationship = {}));

/*
 * Public API Surface of ms-guests
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AddressType, CacheKeys, DefaultValue, EmailType, GuestsUrls, MS_GUESTS_ENV_DEFAULTS, MS_GUESTS_REPLACED_DEFAULTS, MsGuestModule, MsGuestsService, PhoneType, SourceIds, SpinnerKeys, StatusType, TravelPartyGender, TravelPartyRelationship, USHSourceIds, UUIDGenerator };
